// ============================================================================
// GAME RENDERER - Draws the game world
// ============================================================================

import { Renderer } from '@/engine/Renderer';
import { GameWorld } from '@/game/GameWorld';
import { Player } from '@/game/entities/Player';
import { Projectile } from '@/game/entities/Projectile';
import { Grenade } from '@/game/entities/Grenade';
import { Bomb } from '@/game/entities/Bomb';
import { AircraftType } from '@/game/entities/Aircraft';

import { COLORS, HEROES } from '@/shared/constants';
import { lerp, formatTime } from '@/shared/utils';
import { Team, GamePhase, Vector2 } from '@/shared/types';
import { ParticleSystem } from '@/game/effects/ParticleSystem';
import { VisualEffectType } from '@/game/systems/AbilitySystem';
import { EffectType } from '@/game/systems/EffectSystem';

// Track muzzle flashes for visual feedback
interface MuzzleFlash {
  position: Vector2;
  direction: number;
  time: number;
}

// Track bullet tracers (for hitscan visualization)
interface BulletTracer {
  start: Vector2;
  end: Vector2;
  time: number;
  isEnemy: boolean;
}

// Pickup notification for supply drops
interface PickupNotification {
  message: string;
  time: number;
  duration: number;
  color: string;
}

// Confetti particle for victory celebration
interface ConfettiParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  width: number;
  height: number;
  color: string;
  life: number;
  maxLife: number;
}

export class GameRenderer {
  private renderer: Renderer;
  private world: GameWorld;
  private muzzleFlashes: MuzzleFlash[] = [];
  private bulletTracers: BulletTracer[] = [];
  private particleSystem: ParticleSystem;
  
  // Pickup notifications
  private pickupNotifications: PickupNotification[] = [];
  
  // Match end celebration/defeat effects
  private matchEndConfetti: ConfettiParticle[] = [];
  private matchEndStartTime: number = 0;
  private lastMatchEndPhase: boolean = false;
  
  // App version
  private appVersion: string;

  constructor(renderer: Renderer, world: GameWorld, appVersion: string) {
    this.renderer = renderer;
    this.world = world;
    this.appVersion = appVersion;
    this.particleSystem = new ParticleSystem();
  }

  render(alpha: number): void {
    this.renderer.beginFrame();

    // Camera follows local player
    const localPlayer = this.world.getLocalPlayer();
    if (localPlayer) {
      // Interpolate camera position
      const interpX = lerp(localPlayer.prevPosition.x, localPlayer.position.x, alpha);
      const interpY = lerp(localPlayer.prevPosition.y, localPlayer.position.y, alpha);
      this.renderer.setCamera(interpX, interpY, 1);
    } else {
      // Center on map
      this.renderer.setCamera(
        this.world.getMap().getWidth() / 2,
        this.world.getMap().getHeight() / 2,
        0.8
      );
    }

    // Begin world-space rendering
    this.renderer.beginWorld();

    // Draw map
    this.renderMap();

    // Draw ability effects (below players) - old system
    this.renderAbilityEffects();
    
    // Draw new visual effects from AbilitySystem
    this.renderVisualEffects(alpha);

    // Draw players
    for (const player of this.world.getPlayers()) {
      this.renderPlayer(player, alpha, player.id === localPlayer?.id);
    }

    // Draw projectiles
    for (const projectile of this.world.getProjectiles()) {
      this.renderProjectile(projectile, alpha);
    }

    // Draw grenades
    for (const grenade of this.world.getGrenades()) {
      this.renderGrenade(grenade);
    }

    // Draw bombs
    for (const bomb of this.world.getBombs()) {
      this.renderBomb(bomb);
    }

    // Draw supply crates and aircraft
    this.renderSupplyDropSystem(alpha);

    // Draw bullet tracers (for hitscan weapons)
    this.renderBulletTracers();

    // Draw muzzle flashes
    this.renderMuzzleFlashes();

    // Draw particles
    this.particleSystem.render(this.renderer);

    // Draw radio call path indicator (if active)
    this.renderRadioCallIndicator();

    this.renderer.endWorld();

    // Draw UI (screen-space)
    this.renderUI();

    this.renderer.endFrame();
  }

  private renderMap(): void {
    const map = this.world.getMap();
    const tileSize = map.getTileSize();
    const tiles = map.data.tiles;

    // Get visible area
    const offset = this.renderer.getCameraOffset();
    const zoom = this.renderer.getCameraZoom();
    const viewWidth = this.renderer.getWidth() / zoom;
    const viewHeight = this.renderer.getHeight() / zoom;

    const startX = Math.max(0, Math.floor(offset.x / tileSize));
    const startY = Math.max(0, Math.floor(offset.y / tileSize));
    const endX = Math.min(tiles[0]?.length ?? 0, Math.ceil((offset.x + viewWidth) / tileSize) + 1);
    const endY = Math.min(tiles.length, Math.ceil((offset.y + viewHeight) / tileSize) + 1);

    // Draw floor
    this.renderer.drawRect(
      0, 0,
      map.getWidth(), map.getHeight(),
      { fill: '#151520' }
    );

    // Draw grid
    this.renderer.drawDebugGrid(tileSize, '#1a1a2a');

    // Draw walls
    for (let y = startY; y < endY; y++) {
      const row = tiles[y];
      if (!row) continue;
      
      for (let x = startX; x < endX; x++) {
        const tile = row[x];
        if (tile === 1) {
          this.renderer.drawRect(
            x * tileSize,
            y * tileSize,
            tileSize,
            tileSize,
            { fill: '#2a2a3a', stroke: '#3a3a4a', strokeWidth: 1 }
          );
        }
      }
    }

    // Draw spike sites
    for (let i = 0; i < map.data.spikeSites.length; i++) {
      const site = map.data.spikeSites[i]!;
      this.renderer.drawNeonRect(
        site,
        i === 0 ? COLORS.CYAN : COLORS.MAGENTA,
        0.5
      );
      
      this.renderer.drawText(
        `SITE ${String.fromCharCode(65 + i)}`,
        site.x + site.width / 2,
        site.y + site.height / 2,
        { color: '#fff', align: 'center', baseline: 'middle', size: 16 }
      );
    }

    // Draw planted spike
    const objectives = this.world.getObjectives();
    if (objectives.spikePlanted && objectives.spikePosition) {
      const spikeX = objectives.spikePosition.x;
      const spikeY = objectives.spikePosition.y;
      const pulse = Math.sin(this.world.getAbsoluteTime() * 0.01) * 0.3 + 0.7;
      
      // Outer glow
      this.renderer.drawCircle(spikeX, spikeY, 25 + pulse * 5, {
        fill: `rgba(255, 50, 50, ${pulse * 0.3})`,
        glow: true,
        glowColor: '#ff0000',
        glowSize: 20,
      });
      
      // Spike device (hexagon-ish)
      this.renderer.drawCircle(spikeX, spikeY, 15, {
        fill: '#ff2222',
        stroke: '#ff6666',
        strokeWidth: 3,
        glow: true,
        glowColor: '#ff0000',
        glowSize: 15,
      });
      
      // Inner core
      this.renderer.drawCircle(spikeX, spikeY, 6, {
        fill: '#ffffff',
        glow: true,
        glowColor: '#ff0000',
        glowSize: 8,
      });
      
      // "SPIKE" text
      this.renderer.drawText(
        '💣 SPIKE',
        spikeX, spikeY - 35,
        { color: '#ff4444', align: 'center', size: 12 }
      );
      
      // Upload progress bar
      const barWidth = 60;
      const barHeight = 8;
      const barX = spikeX - barWidth / 2;
      const barY = spikeY + 25;
      
      // Background
      this.renderer.drawRect(barX, barY, barWidth, barHeight, {
        fill: 'rgba(0, 0, 0, 0.7)',
        stroke: '#ff4444',
        strokeWidth: 1,
      });
      
      // Progress fill
      const progress = objectives.spikeProgress;
      if (progress > 0) {
        this.renderer.drawRect(barX + 1, barY + 1, (barWidth - 2) * progress, barHeight - 2, {
          fill: progress > 0.7 ? '#ff0000' : '#ff4444',
        });
      }
    }
  }

  private renderPlayer(player: Player, alpha: number, isLocal: boolean): void {
    // Interpolate position
    const x = lerp(player.prevPosition.x, player.position.x, alpha);
    const y = lerp(player.prevPosition.y, player.position.y, alpha);
    const rotation = player.rotation; // TODO: interpolate rotation

    // Player body color based on team
    const bodyColor = player.team === Team.Attackers ? COLORS.ATTACKER : COLORS.DEFENDER;
    const glowColor = isLocal ? COLORS.CYAN : bodyColor;

    // Handle invisibility (cloak)
    const isInvisible = player.isInvisible && !isLocal;
    if (isInvisible) {
      // Don't render invisible enemies
      return;
    }

    // Handle dead players - fade out corpse after death
    if (!player.alive) {
      const timeSinceDeath = this.world.getAbsoluteTime() - player.deathTime;
      const fadeOutDuration = 1000; // 1 second fade out
      
      // Don't render if fully faded
      if (timeSinceDeath > fadeOutDuration) {
        return;
      }
      
      // Calculate fade alpha
      const fadeAlpha = 1 - (timeSinceDeath / fadeOutDuration);
      const corpseColor = `rgba(51, 51, 51, ${fadeAlpha})`;
      
      // Draw fading corpse
      this.renderer.drawCircle(x, y, 16, {
        fill: corpseColor,
        stroke: `rgba(100, 100, 100, ${fadeAlpha * 0.5})`,
        strokeWidth: 2,
      });
      return;
    }

    // Draw player circle (alive)
    this.renderer.drawCircle(x, y, 16, {
      fill: player.isInvisible ? `${bodyColor}4D` : bodyColor,
      stroke: player.isInvisible ? `${glowColor}4D` : glowColor,
      strokeWidth: isLocal ? 3 : 2,
      glow: isLocal && !player.isInvisible,
      glowColor: glowColor,
      glowSize: 10,
    });

    // Draw facing direction
    if (player.alive) {
      const facingX = x + Math.cos(rotation) * 20;
      const facingY = y + Math.sin(rotation) * 20;
      
      this.renderer.drawLine(x, y, facingX, facingY, {
        color: player.isInvisible ? 'rgba(255,255,255,0.3)' : '#fff',
        width: 2,
      });

      // Draw weapon aim indicator
      const aimX = x + Math.cos(rotation) * 30;
      const aimY = y + Math.sin(rotation) * 30;
      
      this.renderer.drawCircle(aimX, aimY, 4, {
        fill: player.isInvisible ? `${COLORS.CYAN}4D` : COLORS.CYAN,
        glow: !player.isInvisible,
        glowColor: COLORS.CYAN,
        glowSize: 5,
      });

      // Draw stun indicator
      if (player.isStunned) {
        this.renderer.drawCircle(x, y - 25, 8, {
          fill: 'rgba(255, 255, 0, 0.8)',
          glow: true,
          glowColor: COLORS.YELLOW,
          glowSize: 10,
        });
      }

      // Draw marked indicator (target symbol) - for enemies marked by Zero
      const markedEffect = this.world.getEffectSystem().getEffect(player.id, EffectType.Marked);
      if (markedEffect) {
        const pulse = Math.sin(this.world.getAbsoluteTime() * 0.01) * 0.3 + 0.7;
        
        // Draw pulsing red ring around the marked player
        this.renderer.drawCircle(x, y, 28 + pulse * 5, {
          stroke: `rgba(255, 50, 50, ${pulse})`,
          strokeWidth: 3,
          glow: true,
          glowColor: '#ff0000',
          glowSize: 15,
        });
        
        // Draw crosshair/target above player
        const targetY = y - 55;
        this.renderer.drawCircle(x, targetY, 10, {
          stroke: '#ff4444',
          strokeWidth: 2,
          glow: true,
          glowColor: '#ff0000',
          glowSize: 10,
        });
        // Crosshair lines
        this.renderer.drawLine(x - 16, targetY, x - 6, targetY, { color: '#ff4444', width: 2 });
        this.renderer.drawLine(x + 6, targetY, x + 16, targetY, { color: '#ff4444', width: 2 });
        this.renderer.drawLine(x, targetY - 16, x, targetY - 6, { color: '#ff4444', width: 2 });
        this.renderer.drawLine(x, targetY + 6, x, targetY + 16, { color: '#ff4444', width: 2 });
        
        // Draw "MARKED" text
        this.renderer.drawText(
          '🎯 MARKED',
          x, y - 72,
          { color: '#ff4444', align: 'center', size: 10 }
        );
      }
    }

    // Draw health bar above player
    this.renderer.drawHealthBar(
      x - 20, y - 30,
      40, 6,
      player.health, player.maxHealth,
      player.shield, player.maxShield
    );

    // Draw name
    this.renderer.drawText(
      player.name,
      x, y - 40,
      { color: '#fff', align: 'center', size: 10 }
    );

    // Draw hero icon/indicator
    const heroDef = HEROES[player.heroId];
    this.renderer.drawText(
      heroDef.name.charAt(0),
      x, y,
      { 
        color: '#fff', 
        align: 'center', 
        baseline: 'middle', 
        size: 14,
        glow: true,
        glowColor: '#000',
        glowSize: 2,
      }
    );

    // Draw spike carrier indicator (Data Spike mode)
    const objectives = this.world.getObjectives();
    if (objectives.spikeCarrierId === player.id && !objectives.spikePlanted) {
      const pulse = Math.sin(this.world.getAbsoluteTime() * 0.008) * 0.2 + 0.8;
      
      // Spike icon above player
      this.renderer.drawText(
        '💣',
        x + 25, y - 10,
        { color: '#ff4444', align: 'center', size: 14 }
      );
      
      // Glow ring to indicate carrier
      this.renderer.drawCircle(x, y, 22, {
        stroke: `rgba(255, 100, 50, ${pulse})`,
        strokeWidth: 2,
      });
    }

    // Draw planting progress bar
    // Use peerId for comparison since entity IDs differ between host and client
    const isPlanting = (objectives.plantingPeerId === player.peerId || objectives.plantingPlayerId === player.id) && objectives.plantingProgress > 0;
    if (isPlanting) {
      const barWidth = 50;
      const barHeight = 6;
      const barX = x - barWidth / 2;
      const barY = y + 30;
      
      // Background
      this.renderer.drawRect(barX, barY, barWidth, barHeight, {
        fill: 'rgba(0, 0, 0, 0.8)',
        stroke: '#ff8800',
        strokeWidth: 1,
      });
      
      // Progress
      this.renderer.drawRect(barX + 1, barY + 1, (barWidth - 2) * objectives.plantingProgress, barHeight - 2, {
        fill: '#ff8800',
      });
      
      // Label
      this.renderer.drawText(
        'PLANTING...',
        x, barY + barHeight + 10,
        { color: '#ff8800', align: 'center', size: 8 }
      );
    }

    // Draw defusing progress bar
    // Use peerId for comparison since entity IDs differ between host and client
    const isDefusing = (objectives.defusingPeerId === player.peerId || objectives.defusingPlayerId === player.id) && objectives.defusingProgress > 0;
    if (isDefusing) {
      const barWidth = 50;
      const barHeight = 6;
      const barX = x - barWidth / 2;
      const barY = y + 30;
      
      // Background
      this.renderer.drawRect(barX, barY, barWidth, barHeight, {
        fill: 'rgba(0, 0, 0, 0.8)',
        stroke: '#00ff88',
        strokeWidth: 1,
      });
      
      // Progress
      this.renderer.drawRect(barX + 1, barY + 1, (barWidth - 2) * objectives.defusingProgress, barHeight - 2, {
        fill: '#00ff88',
      });
      
      // Label
      this.renderer.drawText(
        'DEFUSING...',
        x, barY + barHeight + 10,
        { color: '#00ff88', align: 'center', size: 8 }
      );
    }
  }

  private renderProjectile(projectile: Projectile, _alpha: number): void {
    const x = projectile.position.x;
    const y = projectile.position.y;

    // Draw projectile as a glowing circle
    this.renderer.drawCircle(x, y, 4, {
      fill: COLORS.YELLOW,
      glow: true,
      glowColor: COLORS.YELLOW,
      glowSize: 8,
    });

    // Draw trail
    const trailX = x - projectile.velocity.x * 0.02;
    const trailY = y - projectile.velocity.y * 0.02;
    this.renderer.drawLine(trailX, trailY, x, y, {
      color: COLORS.YELLOW,
      width: 2,
    });
  }

  private renderGrenade(grenade: Grenade): void {
    const x = grenade.position.x;
    const y = grenade.position.y;
    const time = performance.now();
    
    // Pulsing warning effect as fuse runs down
    const fusePercent = grenade.fuseTime / 3000;
    const pulseSpeed = 5 - fusePercent * 4; // Faster pulse as time runs out
    const pulse = Math.abs(Math.sin(time / (100 / pulseSpeed)));
    
    // Draw shadow when grenade is airborne (top-down view)
    // Access private property via type assertion
    const grenadeAny = grenade as any;
    if (grenadeAny.isAirborne && grenadeAny.flightTimer > 0) {
      // Shadow grows as grenade gets closer to landing
      const flightPercent = 1 - (grenadeAny.flightTimer / 800);
      const shadowSize = 4 + flightPercent * 8;
      const shadowAlpha = 0.2 + flightPercent * 0.3;
      
      this.renderer.drawCircle(x, y, shadowSize, {
        fill: `rgba(0, 0, 0, ${shadowAlpha})`,
      });
    }
    
    // Draw grenade body
    this.renderer.drawCircle(x, y, 8, {
      fill: '#2a2a2a',
      stroke: '#ff4444',
      strokeWidth: 2,
    });
    
    // Draw danger indicator (gets brighter as fuse runs low)
    const dangerColor = fusePercent > 0.5 ? '#ffaa00' : '#ff0000';
    const dangerSize = 4 + pulse * 6;
    this.renderer.drawCircle(x, y, dangerSize, {
      fill: dangerColor,
      glow: true,
      glowColor: dangerColor,
      glowSize: 8 + pulse * 12,
    });
    
    // Draw fuse indicator line
    const fuseLength = 12;
    const fuseX = x + Math.cos(grenade.rotation) * fuseLength;
    const fuseY = y + Math.sin(grenade.rotation) * fuseLength;
    this.renderer.drawLine(x, y, fuseX, fuseY, {
      color: dangerColor,
      width: 2,
    });
  }

  private renderRadioCallIndicator(): void {
    const localPlayer = this.world.getLocalPlayer();
    if (!localPlayer) return;
    
    const radioState = this.world.getRadioCallState(localPlayer.id);
    if (!radioState || !radioState.firstClick || !radioState.awaitingSecondClick) return;
    
    const start = radioState.firstClick;
    const currentAim = { x: localPlayer.input.aimX, y: localPlayer.input.aimY };
    
    if (radioState.type === 'bomb') {
      // Draw bomber direction line (dashed)
      const dx = currentAim.x - start.x;
      const dy = currentAim.y - start.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      
      if (distance > 20) {
        // Draw dashed line
        const dashLength = 20;
        const gapLength = 15;
        const totalLength = dashLength + gapLength;
        const numDashes = Math.floor(distance / totalLength);
        
        for (let i = 0; i < numDashes; i++) {
          const startDist = i * totalLength;
          const endDist = startDist + dashLength;
          const x1 = start.x + Math.cos(angle) * startDist;
          const y1 = start.y + Math.sin(angle) * startDist;
          const x2 = start.x + Math.cos(angle) * endDist;
          const y2 = start.y + Math.sin(angle) * endDist;
          
          this.renderer.drawLine(x1, y1, x2, y2, {
            color: COLORS.MAGENTA,
            width: 3
          });
        }
        
        // Draw big arrow at end showing direction
        const arrowSize = 30;
        const arrowX = currentAim.x;
        const arrowY = currentAim.y;
        
        const x1 = arrowX + Math.cos(angle) * arrowSize;
        const y1 = arrowY + Math.sin(angle) * arrowSize;
        const x2 = arrowX + Math.cos(angle + Math.PI * 0.7) * arrowSize * 0.8;
        const y2 = arrowY + Math.sin(angle + Math.PI * 0.7) * arrowSize * 0.8;
        const x3 = arrowX + Math.cos(angle - Math.PI * 0.7) * arrowSize * 0.8;
        const y3 = arrowY + Math.sin(angle - Math.PI * 0.7) * arrowSize * 0.8;
        
        this.renderer.drawPolygon([
          { x: x1, y: y1 },
          { x: x2, y: y2 },
          { x: x3, y: y3 }
        ], { 
          fill: COLORS.MAGENTA, 
          stroke: '#FFFFFF', 
          strokeWidth: 2,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 15
        });
      }
      
      // Draw start marker
      this.renderer.drawCircle(start.x, start.y, 12, {
        stroke: COLORS.MAGENTA,
        strokeWidth: 3,
        glow: true,
        glowColor: COLORS.MAGENTA,
        glowSize: 10,
      });
      
      // Text label
      this.renderer.drawText(
        'BOMBING RUN',
        (start.x + currentAim.x) / 2,
        (start.y + currentAim.y) / 2 - 30,
        { 
          color: COLORS.MAGENTA, 
          align: 'center', 
          size: 16
        }
      );
    } else if (radioState.type === 'missile') {
      // Missile strike targeting - show laser beam and target area
      const targetX = localPlayer.input.aimX;
      const targetY = localPlayer.input.aimY;
      const time = performance.now();
      
      // Pulsing target circle
      const pulseSize = 250 + Math.sin(time / 150) * 20;
      
      // Outer strike zone
      this.renderer.drawCircle(targetX, targetY, pulseSize, {
        stroke: '#ff6600',
        strokeWidth: 3,
        glow: true,
        glowColor: '#ff6600',
        glowSize: 15,
      });
      
      // Inner target zone
      this.renderer.drawCircle(targetX, targetY, pulseSize * 0.5, {
        stroke: '#ff3300',
        strokeWidth: 2,
        glow: true,
        glowColor: '#ff3300',
        glowSize: 10,
      });
      
      // Center crosshair
      const crossSize = 15;
      this.renderer.drawLine(
        targetX - crossSize, targetY,
        targetX + crossSize, targetY,
        { color: '#ff0000', width: 2 }
      );
      this.renderer.drawLine(
        targetX, targetY - crossSize,
        targetX, targetY + crossSize,
        { color: '#ff0000', width: 2 }
      );
      
      // Laser targeting beams from corners (like satellite targeting)
      const beamLength = 300;
      const beamAlpha = 0.3 + Math.sin(time / 100) * 0.2;
      
      // Four laser beams converging on target
      const corners = [
        { angle: -Math.PI * 0.25 }, // Top-right
        { angle: Math.PI * 0.25 },  // Bottom-right
        { angle: Math.PI * 0.75 },  // Bottom-left
        { angle: -Math.PI * 0.75 }, // Top-left
      ];
      
      for (const corner of corners) {
        const startX = targetX + Math.cos(corner.angle) * beamLength;
        const startY = targetY + Math.sin(corner.angle) * beamLength;
        
        // Draw laser beam
        this.renderer.drawLine(startX, startY, targetX, targetY, {
          color: `rgba(255, 0, 0, ${beamAlpha})`,
          width: 2,
        });
        
        // Bright core
        this.renderer.drawLine(startX, startY, targetX, targetY, {
          color: `rgba(255, 200, 200, ${beamAlpha * 0.7})`,
          width: 1,
        });
      }
      
      // Warning text
      this.renderer.drawText(
        'MISSILE STRIKE',
        targetX,
        targetY - pulseSize - 30,
        { 
          color: '#ff6600', 
          align: 'center', 
          size: 18
        }
      );
      
      this.renderer.drawText(
        'INCOMING',
        targetX,
        targetY - pulseSize - 50,
        { 
          color: '#ff0000', 
          align: 'center', 
          size: 14
        }
      );
    }
  }

  private renderBomb(bomb: Bomb): void {
    const x = bomb.position.x;
    const y = bomb.position.y;
    
    // Check if this is a missile with trajectory data
    const isMissile = bomb.launchPosition && bomb.targetPosition && bomb.launchTime !== undefined;
    
    // Draw exhaust trail for missiles (white smoke that fades)
    if (isMissile && bomb.trail.length > 1) {
      for (let i = 0; i < bomb.trail.length - 1; i++) {
        const t1 = bomb.trail[i];
        const t2 = bomb.trail[i + 1];
        if (!t1 || !t2) continue;
        const alpha = (i / bomb.trail.length) * 0.6; // Fade older trail
        const width = 3 + (i / bomb.trail.length) * 2; // Get thicker towards missile
        
        this.renderer.drawLine(t1.x, t1.y, t2.x, t2.y, {
          color: `rgba(255, 255, 255, ${alpha})`,
          width: width,
        });
      }
    }
    
    // Visual size based on altitude (perspective effect - gets smaller as it's higher)
    const altitudeScale = 0.3 + (1 - bomb.altitude / 1200) * 0.7; // Smaller when high, larger when low
    
    if (isMissile) {
      // Missile rendering - elongated, pointed rocket
      const missileLength = 18 * altitudeScale;
      const missileWidth = 5 * altitudeScale;
      
      // Calculate missile angle based on trajectory
      let angle = -Math.PI / 2; // Default pointing down
      if (bomb.trail.length >= 2) {
        const prev = bomb.trail[bomb.trail.length - 2];
        const curr = bomb.position;
        if (prev) {
          angle = Math.atan2(curr.y - prev.y, curr.x - prev.x);
        }
      }
      
      // Draw shadow on ground
      const shadowAlpha = Math.max(0, 1 - (bomb.altitude / 1200));
      this.renderer.drawCircle(x, y, 8 + shadowAlpha * 8, {
        fill: `rgba(0, 0, 0, ${shadowAlpha * 0.5})`,
      });
      
      // Rotate missile to face direction of travel
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      // Missile body (pointed in direction of travel)
      const nose = { x: x + cos * missileLength / 2, y: y + sin * missileLength / 2 };
      const leftSide = { x: x - sin * missileWidth / 2, y: y + cos * missileWidth / 2 };
      const rightSide = { x: x + sin * missileWidth / 2, y: y - cos * missileWidth / 2 };
      const leftFin = { 
        x: x - cos * missileLength / 3 - sin * missileWidth / 3, 
        y: y - sin * missileLength / 3 + cos * missileWidth / 3 
      };
      const rightFin = { 
        x: x - cos * missileLength / 3 + sin * missileWidth / 3, 
        y: y - sin * missileLength / 3 - cos * missileWidth / 3 
      };
      const tail = { x: x - cos * missileLength / 3, y: y - sin * missileLength / 3 };
      
      this.renderer.drawPolygon([nose, leftSide, leftFin, tail, rightFin, rightSide], {
        fill: '#ff6600',
        stroke: '#ff3300',
        strokeWidth: 2,
      });
      
      // Warhead glow
      this.renderer.drawCircle(
        nose.x - cos * missileLength * 0.3, 
        nose.y - sin * missileLength * 0.3, 
        missileWidth * 0.5, 
        {
          fill: '#ffaa00',
          glow: true,
          glowColor: '#ff6600',
          glowSize: 10 * altitudeScale,
        }
      );
      
      // Thrust effect at tail
      if (bomb.altitude > 20) {
        const thrustLength = 15 + altitudeScale * 15;
        const thrustAlpha = Math.min(0.9, bomb.altitude / 200);
        
        // Orange thrust flame
        this.renderer.drawLine(
          tail.x, tail.y,
          tail.x - cos * thrustLength, tail.y - sin * thrustLength,
          {
            color: `rgba(255, 150, 50, ${thrustAlpha})`,
            width: 5 * altitudeScale,
          }
        );
        // White hot core
        this.renderer.drawLine(
          tail.x, tail.y,
          tail.x - cos * thrustLength * 0.6, tail.y - sin * thrustLength * 0.6,
          {
            color: `rgba(255, 255, 200, ${thrustAlpha * 0.9})`,
            width: 3 * altitudeScale,
          }
        );
      }
    } else {
      // Bomb rendering - round, from aircraft, with perspective
      const bombSize = 8 + altitudeScale * 10; // Gets bigger as it falls
      
      // Draw shadow on ground (gets sharper and darker as bomb falls)
      const groundShadowAlpha = Math.max(0, 1 - (bomb.altitude / 1200));
      const shadowSize = 8 + groundShadowAlpha * 12;
      
      // Soft outer shadow
      this.renderer.drawCircle(x, y, shadowSize * 1.5, {
        fill: `rgba(0, 0, 0, ${groundShadowAlpha * 0.2})`,
      });
      
      // Main shadow
      this.renderer.drawCircle(x, y, shadowSize, {
        fill: `rgba(0, 0, 0, ${groundShadowAlpha * 0.5})`,
      });
      
      // Draw bomb body (gets more visible as it gets closer)
      const bombAlpha = 0.6 + groundShadowAlpha * 0.4;
      
      // Outer bomb shell
      this.renderer.drawCircle(x, y, bombSize, {
        fill: `rgba(40, 40, 40, ${bombAlpha})`,
        stroke: `rgba(60, 60, 60, ${bombAlpha})`,
        strokeWidth: 2 * altitudeScale,
      });
      
      // Bomb details (fins/markings) - only visible when close
      if (altitudeScale > 0.6) {
        // Warning stripe
        this.renderer.drawCircle(x, y, bombSize * 0.7, {
          fill: `rgba(255, 200, 0, ${groundShadowAlpha * 0.6})`,
        });
      }
      
      // Inner explosive core (glows more as it falls)
      this.renderer.drawCircle(x, y, bombSize * 0.4, {
        fill: `rgba(255, 100, 0, ${bombAlpha})`,
        glow: true,
        glowColor: '#ff6600',
        glowSize: bombSize * 0.5,
      });
      
      // Falling trail (smoke/whistle trail) - gets more visible as it falls
      if (bomb.altitude > 30) {
        const trailLength = 15 + (400 - bomb.altitude) / 10; // Trail gets longer as it falls
        const trailAlpha = Math.min(0.7, (400 - bomb.altitude) / 300);
        
        // Determine trail direction (opposite of velocity or straight up if no velocity)
        const hasVelocity = Math.abs(bomb.velocity.x) > 0.01 || Math.abs(bomb.velocity.y) > 0.01;
        const trailEndX = hasVelocity ? x - bomb.velocity.x * 100 : x;
        const trailEndY = hasVelocity ? y - bomb.velocity.y * 100 : y - trailLength;
        
        // Smoke trail
        this.renderer.drawLine(x, y, trailEndX, trailEndY, {
          color: `rgba(120, 120, 120, ${trailAlpha})`,
          width: 2 + altitudeScale * 2,
        });
        
        // Inner bright trail
        this.renderer.drawLine(x, y, 
          x + (trailEndX - x) * 0.5, 
          y + (trailEndY - y) * 0.5, {
          color: `rgba(180, 180, 180, ${trailAlpha * 0.7})`,
          width: 1 + altitudeScale,
        });
      }
    }
  }

  private renderSupplyDropSystem(_alpha: number): void {
    const time = performance.now();
    
    // Render aircraft with shadows (top-down view)
    for (const aircraft of this.world.getAircraft()) {
      // Calculate altitude scale effect (higher = appears smaller)
      const altitudeScale = 1 + (aircraft.altitude / 500); // Scale up with altitude
      
      // Realistic shadow: distant, offset based on sun angle, softer with altitude
      const sunAngle = Math.PI / 4; // 45 degree angle
      const shadowDistance = aircraft.altitude * 0.8; // Shadow is far from aircraft
      const shadowOffsetX = Math.cos(sunAngle) * shadowDistance;
      const shadowOffsetY = Math.sin(sunAngle) * shadowDistance;
      
      // Shadow gets softer and larger with altitude
      const shadowBlur = aircraft.altitude / 10; // More blur when higher
      const shadowAlpha = Math.max(0.15, 0.5 - aircraft.altitude / 2000); // Fainter when higher
      
      const rotation = aircraft.getRotation();
      const shadowX = aircraft.position.x + shadowOffsetX;
      const shadowY = aircraft.position.y + shadowOffsetY;
      
      // Draw plane-shaped shadow instead of circle
      const ctx = this.renderer.getContext();
      ctx.save();
      ctx.translate(shadowX, shadowY);
      ctx.rotate(rotation);
      
      if (aircraft.type === AircraftType.Bomber) {
        // B-52 bomber shadow - large fuselage with wide wings
        // Outer blur layer
        ctx.fillStyle = `rgba(0, 0, 0, ${shadowAlpha * 0.2})`;
        ctx.fillRect(-40 - shadowBlur, -25 - shadowBlur, 80 + shadowBlur * 2, 50 + shadowBlur * 2);
        
        // Main shadow
        ctx.fillStyle = `rgba(0, 0, 0, ${shadowAlpha * 0.6})`;
        // Fuselage
        ctx.fillRect(-35, -8, 70, 16);
        // Wings
        ctx.fillRect(-30, -22, 60, 44);
        // Tail
        ctx.fillRect(-38, -5, 8, 10);
      } else {
        // B-2 Spirit shadow - flying wing design
        // Outer blur
        ctx.fillStyle = `rgba(0, 0, 0, ${shadowAlpha * 0.2})`;
        ctx.beginPath();
        ctx.moveTo(-35 - shadowBlur, 0);
        ctx.lineTo(0, -20 - shadowBlur);
        ctx.lineTo(35 + shadowBlur, 0);
        ctx.lineTo(0, 15 + shadowBlur);
        ctx.closePath();
        ctx.fill();
        
        // Main shadow
        ctx.fillStyle = `rgba(0, 0, 0, ${shadowAlpha * 0.6})`;
        ctx.beginPath();
        ctx.moveTo(-30, 0);
        ctx.lineTo(0, -18);
        ctx.lineTo(30, 0);
        ctx.lineTo(0, 12);
        ctx.closePath();
        ctx.fill();
      }
      
      ctx.restore();
      
      // Render different aircraft based on type
      if (aircraft.type === AircraftType.Bomber) {
        this.renderB52Bomber(aircraft, rotation, altitudeScale, time);
      } else {
        this.renderB2Spirit(aircraft, rotation, altitudeScale, time);
      }
    }
    
    // Render supply drops (glowing orbs)
    for (const crate of this.world.getSupplyCrates()) {
      if (!crate.active) continue;
      
      const glowColor = crate.supplyType === 'weapon' ? COLORS.MAGENTA :
                        crate.supplyType === 'health_kit' ? '#44FF44' :
                        crate.supplyType === 'shield' ? COLORS.CYAN :
                        crate.supplyType === 'credits' ? COLORS.YELLOW :
                        crate.supplyType === 'bomb_call' ? COLORS.MAGENTA :
                        crate.supplyType === 'missile_strike' ? '#ff6600' :
                        '#FF8800';
      
      const innerColor = crate.supplyType === 'weapon' ? '#FF00FF' :
                         crate.supplyType === 'health_kit' ? '#00FF00' :
                         crate.supplyType === 'shield' ? '#00FFFF' :
                         crate.supplyType === 'credits' ? '#FFFF00' :
                         crate.supplyType === 'bomb_call' ? '#FF00FF' :
                         crate.supplyType === 'missile_strike' ? '#ff6600' :
                         '#FFA500';
      
      // Draw parachute if falling (top-down circular view)
      if (crate.parachuteActive && crate.altitude > 0) {
        // Parachute canopy (circular from top-down)
        const chuteSize = 20 + crate.altitude / 10; // Grows larger as it's higher
        
        // Outer parachute ring (fabric)
        this.renderer.drawCircle(
          crate.position.x,
          crate.position.y,
          chuteSize,
          {
            fill: 'rgba(255, 100, 100, 0.3)',
            stroke: 'rgba(255, 50, 50, 0.8)',
            strokeWidth: 2,
          }
        );
        
        // Inner parachute ring
        this.renderer.drawCircle(
          crate.position.x,
          crate.position.y,
          chuteSize * 0.7,
          {
            fill: 'rgba(255, 150, 150, 0.2)',
            stroke: 'rgba(255, 100, 100, 0.6)',
            strokeWidth: 1,
          }
        );
        
        // Parachute center vent hole
        this.renderer.drawCircle(
          crate.position.x,
          crate.position.y,
          chuteSize * 0.2,
          {
            fill: 'rgba(0, 0, 0, 0.3)',
          }
        );
        
        // Suspension lines (radial pattern from top-down)
        for (let i = 0; i < 8; i++) {
          const angle = (i / 8) * Math.PI * 2;
          this.renderer.drawLine(
            crate.position.x + Math.cos(angle) * chuteSize * 0.9,
            crate.position.y + Math.sin(angle) * chuteSize * 0.9,
            crate.position.x,
            crate.position.y,
            {
              color: 'rgba(200, 200, 200, 0.6)',
              width: 1,
            }
          );
        }
      }
      
      // Ground shadow (grows as orb gets closer)
      const shadowSize = 15 + (200 - crate.altitude) / 10;
      const shadowAlpha = Math.max(0.1, Math.min(0.4, (200 - crate.altitude) / 200));
      this.renderer.drawCircle(
        crate.position.x,
        crate.position.y,
        shadowSize,
        { fill: `rgba(0, 0, 0, ${shadowAlpha})` }
      );
      
      // Glowing orb container (visible item inside)
      const orbSize = crate.claimed ? 12 : 18;
      const pulsePhase = (time / 800) % (Math.PI * 2);
      const pulse = 1 + Math.sin(pulsePhase) * 0.15;
      
      // Outer glow aura
      if (!crate.claimed && crate.isLanded()) {
        this.renderer.drawCircle(
          crate.position.x,
          crate.position.y,
          orbSize * 2 * pulse,
          {
            fill: `${glowColor}20`,
          }
        );
      }
      
      // Main orb sphere
      this.renderer.drawCircle(
        crate.position.x,
        crate.position.y,
        orbSize,
        {
          fill: crate.claimed ? '#333333' : `${glowColor}40`,
          stroke: crate.claimed ? '#555555' : glowColor,
          strokeWidth: 2,
          glow: !crate.claimed,
          glowColor: glowColor,
          glowSize: 20 * pulse,
        }
      );
      
      // Inner glow core
      if (!crate.claimed) {
        this.renderer.drawCircle(
          crate.position.x,
          crate.position.y,
          orbSize * 0.7,
          {
            fill: innerColor,
            glow: true,
            glowColor: innerColor,
            glowSize: 8,
          }
        );
      }
      
      // Item icon visible through the orb
      if (!crate.claimed) {
        let iconText = '?';
        if (crate.supplyType === 'weapon') iconText = '⚔️';
        else if (crate.supplyType === 'health_kit') iconText = '🟥➕'; // Red square + plus = first aid
        else if (crate.supplyType === 'shield') iconText = '🛡️';
        else if (crate.supplyType === 'credits') iconText = '💰';
        else if (crate.supplyType === 'grenade') iconText = '💣';
        
        this.renderer.drawText(
          iconText,
          crate.position.x,
          crate.position.y,
          {
            color: '#FFFFFF',
            align: 'center',
            size: 14,
          }
        );
      }
      
      // Floating particles around the orb (if landed)
      if (crate.isLanded() && !crate.claimed) {
        for (let i = 0; i < 3; i++) {
          const particleAngle = (time / 1500) + (i * Math.PI * 2 / 3);
          const particleRadius = orbSize + 8;
          const px = crate.position.x + Math.cos(particleAngle) * particleRadius;
          const py = crate.position.y + Math.sin(particleAngle) * particleRadius;
          
          this.renderer.drawCircle(px, py, 2, {
            fill: glowColor,
            glow: true,
            glowColor: glowColor,
            glowSize: 4,
          });
        }
      }
    }
  }

  private renderB2Spirit(aircraft: any, rotation: number, _altitudeScale: number, time: number): void {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    
    // B-2 Spirit flying wing body shape (top-down)
    const bodyPoints = [
        // Nose (center front)
        { x: aircraft.position.x + cos * 30, y: aircraft.position.y + sin * 30 },
        // Right wing leading edge (smooth curve approximation)
        { x: aircraft.position.x + cos * 25 - sin * 15, y: aircraft.position.y + sin * 25 + cos * 15 },
        { x: aircraft.position.x + cos * 15 - sin * 35, y: aircraft.position.y + sin * 15 + cos * 35 },
        { x: aircraft.position.x + cos * 0 - sin * 45, y: aircraft.position.y + sin * 0 + cos * 45 },
        // Right wing tip
        { x: aircraft.position.x - cos * 10 - sin * 48, y: aircraft.position.y - sin * 10 + cos * 48 },
        // Right trailing edge (jagged)
        { x: aircraft.position.x - cos * 15 - sin * 45, y: aircraft.position.y - sin * 15 + cos * 45 },
        { x: aircraft.position.x - cos * 25 - sin * 35, y: aircraft.position.y - sin * 25 + cos * 35 },
        // Right exhaust notch
        { x: aircraft.position.x - cos * 28 - sin * 22, y: aircraft.position.y - sin * 28 + cos * 22 },
        { x: aircraft.position.x - cos * 32 - sin * 18, y: aircraft.position.y - sin * 32 + cos * 18 },
        // Center trailing edge
        { x: aircraft.position.x - cos * 35 - sin * 8, y: aircraft.position.y - sin * 35 + cos * 8 },
        { x: aircraft.position.x - cos * 35, y: aircraft.position.y - sin * 35 },
        { x: aircraft.position.x - cos * 35 + sin * 8, y: aircraft.position.y - sin * 35 - cos * 8 },
        // Left exhaust notch
        { x: aircraft.position.x - cos * 32 + sin * 18, y: aircraft.position.y - sin * 32 - cos * 18 },
        { x: aircraft.position.x - cos * 28 + sin * 22, y: aircraft.position.y - sin * 28 - cos * 22 },
        // Left trailing edge (jagged)
        { x: aircraft.position.x - cos * 25 + sin * 35, y: aircraft.position.y - sin * 25 - cos * 35 },
        { x: aircraft.position.x - cos * 15 + sin * 45, y: aircraft.position.y - sin * 15 - cos * 45 },
        // Left wing tip
        { x: aircraft.position.x - cos * 10 + sin * 48, y: aircraft.position.y - sin * 10 - cos * 48 },
        // Left wing leading edge (smooth curve approximation)
        { x: aircraft.position.x + cos * 0 + sin * 45, y: aircraft.position.y + sin * 0 - cos * 45 },
        { x: aircraft.position.x + cos * 15 + sin * 35, y: aircraft.position.y + sin * 15 - cos * 35 },
        { x: aircraft.position.x + cos * 25 + sin * 15, y: aircraft.position.y + sin * 25 - cos * 15 },
      ];
      
      // Main body fill (dark stealth coating)
      this.renderer.drawPolygon(bodyPoints, {
        fill: '#0a0a12',
        stroke: COLORS.CYAN,
        strokeWidth: 1.5,
      });
      
      // Center fuselage highlight
      this.renderer.drawLine(
        aircraft.position.x + cos * 28,
        aircraft.position.y + sin * 28,
        aircraft.position.x - cos * 30,
        aircraft.position.y - sin * 30,
        { color: '#1a1a2e', width: 3 }
      );
      
      // Cockpit window (small glowing area at front)
      this.renderer.drawCircle(
        aircraft.position.x + cos * 22,
        aircraft.position.y + sin * 22,
        5,
        {
          fill: '#0CE5F0',
          glow: true,
          glowColor: COLORS.CYAN,
          glowSize: 8,
        }
      );
      
      // Engine exhaust ports (4 engines, subtle glow)
      const thrustPulse = 0.6 + Math.sin(time / 100) * 0.4;
      
      // Right outer engine
      this.renderer.drawCircle(
        aircraft.position.x - cos * 30 - sin * 20,
        aircraft.position.y - sin * 30 + cos * 20,
        3,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 10 * thrustPulse,
        }
      );
      
      // Right inner engine
      this.renderer.drawCircle(
        aircraft.position.x - cos * 32 - sin * 10,
        aircraft.position.y - sin * 32 + cos * 10,
        3,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 10 * thrustPulse,
        }
      );
      
      // Left inner engine
      this.renderer.drawCircle(
        aircraft.position.x - cos * 32 + sin * 10,
        aircraft.position.y - sin * 32 - cos * 10,
        3,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 10 * thrustPulse,
        }
      );
      
      // Left outer engine
      this.renderer.drawCircle(
        aircraft.position.x - cos * 30 + sin * 20,
        aircraft.position.y - sin * 30 - cos * 20,
        3,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 10 * thrustPulse,
        }
      );
      
      // Engine exhaust trails (adds visibility and motion)
      const trailLength = 40;
      const trailFade = 0.4 * thrustPulse;
      
      // Right outer trail
      this.renderer.drawLine(
        aircraft.position.x - cos * 30 - sin * 20,
        aircraft.position.y - sin * 30 + cos * 20,
        aircraft.position.x - cos * (30 + trailLength) - sin * 20,
        aircraft.position.y - sin * (30 + trailLength) + cos * 20,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3 }
      );
      
      // Right inner trail
      this.renderer.drawLine(
        aircraft.position.x - cos * 32 - sin * 10,
        aircraft.position.y - sin * 32 + cos * 10,
        aircraft.position.x - cos * (32 + trailLength) - sin * 10,
        aircraft.position.y - sin * (32 + trailLength) + cos * 10,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3 }
      );
      
      // Left inner trail
      this.renderer.drawLine(
        aircraft.position.x - cos * 32 + sin * 10,
        aircraft.position.y - sin * 32 - cos * 10,
        aircraft.position.x - cos * (32 + trailLength) + sin * 10,
        aircraft.position.y - sin * (32 + trailLength) - cos * 10,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3 }
      );
      
      // Left outer trail
      this.renderer.drawLine(
        aircraft.position.x - cos * 30 + sin * 20,
        aircraft.position.y - sin * 30 - cos * 20,
        aircraft.position.x - cos * (30 + trailLength) + sin * 20,
        aircraft.position.y - sin * (30 + trailLength) - cos * 20,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3 }
      );
      
      // Wing tip navigation lights (subtle)
      this.renderer.drawCircle(
        aircraft.position.x - cos * 8 - sin * 46,
        aircraft.position.y - sin * 8 + cos * 46,
        2,
        {
          fill: '#FF4444',
          glow: true,
          glowColor: '#FF4444',
          glowSize: 4,
        }
      );
      this.renderer.drawCircle(
        aircraft.position.x - cos * 8 + sin * 46,
        aircraft.position.y - sin * 8 - cos * 46,
        2,
        {
          fill: '#44FF44',
          glow: true,
          glowColor: '#44FF44',
          glowSize: 4,
        }
      );
  }

  private renderB52Bomber(aircraft: any, rotation: number, _altitudeScale: number, time: number): void {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    
    // B-52 Stratofortress body shape (larger, more traditional bomber design)
    const bodyPoints = [
        // Nose (center front)
        { x: aircraft.position.x + cos * 40, y: aircraft.position.y + sin * 40 },
        // Right wing leading edge
        { x: aircraft.position.x + cos * 30 - sin * 20, y: aircraft.position.y + sin * 30 + cos * 20 },
        { x: aircraft.position.x + cos * 20 - sin * 50, y: aircraft.position.y + sin * 20 + cos * 50 },
        { x: aircraft.position.x + cos * 0 - sin * 65, y: aircraft.position.y + sin * 0 + cos * 65 },
        // Right wing tip
        { x: aircraft.position.x - cos * 10 - sin * 68, y: aircraft.position.y - sin * 10 + cos * 68 },
        // Right trailing edge
        { x: aircraft.position.x - cos * 20 - sin * 65, y: aircraft.position.y - sin * 20 + cos * 65 },
        { x: aircraft.position.x - cos * 30 - sin * 55, y: aircraft.position.y - sin * 30 + cos * 55 },
        { x: aircraft.position.x - cos * 35 - sin * 30, y: aircraft.position.y - sin * 35 + cos * 30 },
        // Right side fuselage
        { x: aircraft.position.x - cos * 40 - sin * 8, y: aircraft.position.y - sin * 40 + cos * 8 },
        // Tail section
        { x: aircraft.position.x - cos * 45, y: aircraft.position.y - sin * 45 },
        // Left side fuselage
        { x: aircraft.position.x - cos * 40 + sin * 8, y: aircraft.position.y - sin * 40 - cos * 8 },
        { x: aircraft.position.x - cos * 35 + sin * 30, y: aircraft.position.y - sin * 35 - cos * 30 },
        { x: aircraft.position.x - cos * 30 + sin * 55, y: aircraft.position.y - sin * 30 - cos * 55 },
        { x: aircraft.position.x - cos * 20 + sin * 65, y: aircraft.position.y - sin * 20 - cos * 65 },
        // Left wing tip
        { x: aircraft.position.x - cos * 10 + sin * 68, y: aircraft.position.y - sin * 10 - cos * 68 },
        // Left wing leading edge
        { x: aircraft.position.x + cos * 0 + sin * 65, y: aircraft.position.y + sin * 0 - cos * 65 },
        { x: aircraft.position.x + cos * 20 + sin * 50, y: aircraft.position.y + sin * 20 - cos * 50 },
        { x: aircraft.position.x + cos * 30 + sin * 20, y: aircraft.position.y + sin * 30 - cos * 20 },
      ];
      
      // Main body fill (military gray/silver)
      this.renderer.drawPolygon(bodyPoints, {
        fill: '#2a2a3a',
        stroke: COLORS.CYAN,
        strokeWidth: 2,
      });
      
      // Center fuselage highlight
      this.renderer.drawLine(
        aircraft.position.x + cos * 38,
        aircraft.position.y + sin * 38,
        aircraft.position.x - cos * 40,
        aircraft.position.y - sin * 40,
        { color: '#3a3a4a', width: 4 }
      );
      
      // Cockpit window (larger for B-52)
      this.renderer.drawCircle(
        aircraft.position.x + cos * 32,
        aircraft.position.y + sin * 32,
        6,
        {
          fill: '#0CE5F0',
          glow: true,
          glowColor: COLORS.CYAN,
          glowSize: 10,
        }
      );
      
      // Engine exhaust ports (8 engines - 4 per wing)
      const thrustPulse = 0.6 + Math.sin(time / 100) * 0.4;
      
      // Right wing engines (4 engines)
      // Right outer engine
      this.renderer.drawCircle(
        aircraft.position.x - cos * 25 - sin * 58,
        aircraft.position.y - sin * 25 + cos * 58,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 27 - sin * 50,
        aircraft.position.y - sin * 27 + cos * 50,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 32 - sin * 35,
        aircraft.position.y - sin * 32 + cos * 35,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 34 - sin * 27,
        aircraft.position.y - sin * 34 + cos * 27,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      // Left wing engines (4 engines)
      this.renderer.drawCircle(
        aircraft.position.x - cos * 25 + sin * 58,
        aircraft.position.y - sin * 25 - cos * 58,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 27 + sin * 50,
        aircraft.position.y - sin * 27 - cos * 50,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 32 + sin * 35,
        aircraft.position.y - sin * 32 - cos * 35,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 34 + sin * 27,
        aircraft.position.y - sin * 34 - cos * 27,
        3.5,
        {
          fill: COLORS.MAGENTA,
          glow: true,
          glowColor: COLORS.MAGENTA,
          glowSize: 12 * thrustPulse,
        }
      );
      
      // Engine exhaust trails (longer for B-52)
      const trailLength = 50;
      const trailFade = 0.5 * thrustPulse;
      
      // Right wing trails
      this.renderer.drawLine(
        aircraft.position.x - cos * 25 - sin * 58,
        aircraft.position.y - sin * 25 + cos * 58,
        aircraft.position.x - cos * (25 + trailLength) - sin * 58,
        aircraft.position.y - sin * (25 + trailLength) + cos * 58,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 27 - sin * 50,
        aircraft.position.y - sin * 27 + cos * 50,
        aircraft.position.x - cos * (27 + trailLength) - sin * 50,
        aircraft.position.y - sin * (27 + trailLength) + cos * 50,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 32 - sin * 35,
        aircraft.position.y - sin * 32 + cos * 35,
        aircraft.position.x - cos * (32 + trailLength) - sin * 35,
        aircraft.position.y - sin * (32 + trailLength) + cos * 35,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 34 - sin * 27,
        aircraft.position.y - sin * 34 + cos * 27,
        aircraft.position.x - cos * (34 + trailLength) - sin * 27,
        aircraft.position.y - sin * (34 + trailLength) + cos * 27,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      // Left wing trails
      this.renderer.drawLine(
        aircraft.position.x - cos * 25 + sin * 58,
        aircraft.position.y - sin * 25 - cos * 58,
        aircraft.position.x - cos * (25 + trailLength) + sin * 58,
        aircraft.position.y - sin * (25 + trailLength) - cos * 58,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 27 + sin * 50,
        aircraft.position.y - sin * 27 - cos * 50,
        aircraft.position.x - cos * (27 + trailLength) + sin * 50,
        aircraft.position.y - sin * (27 + trailLength) - cos * 50,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 32 + sin * 35,
        aircraft.position.y - sin * 32 - cos * 35,
        aircraft.position.x - cos * (32 + trailLength) + sin * 35,
        aircraft.position.y - sin * (32 + trailLength) - cos * 35,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      this.renderer.drawLine(
        aircraft.position.x - cos * 34 + sin * 27,
        aircraft.position.y - sin * 34 - cos * 27,
        aircraft.position.x - cos * (34 + trailLength) + sin * 27,
        aircraft.position.y - sin * (34 + trailLength) - cos * 27,
        { color: `rgba(255, 0, 255, ${trailFade})`, width: 3.5 }
      );
      
      // Wing tip navigation lights
      this.renderer.drawCircle(
        aircraft.position.x - cos * 8 - sin * 66,
        aircraft.position.y - sin * 8 + cos * 66,
        3,
        {
          fill: '#FF4444',
          glow: true,
          glowColor: '#FF4444',
          glowSize: 6,
        }
      );
      
      this.renderer.drawCircle(
        aircraft.position.x - cos * 8 + sin * 66,
        aircraft.position.y - sin * 8 - cos * 66,
        3,
        {
          fill: '#44FF44',
          glow: true,
          glowColor: '#44FF44',
          glowSize: 6,
        }
      );
  }

  private renderMuzzleFlashes(): void {
    const now = performance.now();
    
    // Filter out old flashes (older than 100ms)
    this.muzzleFlashes = this.muzzleFlashes.filter(flash => now - flash.time < 100);

    for (const flash of this.muzzleFlashes) {
      const age = now - flash.time;
      const alpha = 1 - (age / 100);
      const size = 15 + age * 0.2;

      // Draw muzzle flash
      this.renderer.drawCircle(
        flash.position.x + Math.cos(flash.direction) * 25,
        flash.position.y + Math.sin(flash.direction) * 25,
        size * alpha,
        {
          fill: `rgba(255, 200, 100, ${alpha * 0.8})`,
          glow: true,
          glowColor: COLORS.YELLOW,
          glowSize: 15 * alpha,
        }
      );
    }
  }

  private renderAbilityEffects(): void {
    const effects = this.world.getAbilityEffects();
    const currentTime = this.world.getTime();

    for (const effect of effects) {
      const elapsed = currentTime - effect.startTime;
      const progress = elapsed / effect.duration;
      const alpha = 1 - progress;

      switch (effect.type) {
        case 'shield':
          this.renderShieldEffect(effect, alpha);
          break;
        case 'shockwave':
          this.renderShockwaveEffect(effect, progress);
          break;
        case 'dash':
          this.renderDashEffect(effect, alpha);
          break;
        case 'cloak':
          // Cloak is handled in player rendering
          break;
      }
    }
  }

  private renderShieldEffect(effect: { position: { x: number; y: number }; direction: number; radius?: number }, alpha: number): void {
    const { position, direction, radius = 60 } = effect;
    
    // Draw arc shield in front of player
    const arcStart = direction - Math.PI / 3;
    const arcEnd = direction + Math.PI / 3;
    
    // Draw multiple arcs for shield effect
    for (let i = 0; i < 3; i++) {
      const r = radius + i * 8;
      const a = alpha * (1 - i * 0.2);
      
      this.renderer.drawArc(
        position.x,
        position.y,
        r,
        arcStart,
        arcEnd,
        {
          stroke: `rgba(100, 200, 255, ${a * 0.8})`,
          strokeWidth: 4 - i,
          glow: true,
          glowColor: COLORS.CYAN,
          glowSize: 10,
        }
      );
    }
  }

  private renderShockwaveEffect(effect: { position: { x: number; y: number }; radius?: number }, progress: number): void {
    const { position, radius = 120 } = effect;
    
    // Expanding ring
    const currentRadius = radius * progress;
    const alpha = 1 - progress;
    
    this.renderer.drawCircle(
      position.x,
      position.y,
      currentRadius,
      {
        stroke: `rgba(255, 150, 50, ${alpha * 0.9})`,
        strokeWidth: 6 * (1 - progress),
        glow: true,
        glowColor: '#ff9933',
        glowSize: 20 * alpha,
      }
    );

    // Inner flash
    if (progress < 0.3) {
      const flashAlpha = 1 - (progress / 0.3);
      this.renderer.drawCircle(
        position.x,
        position.y,
        radius * 0.3,
        {
          fill: `rgba(255, 200, 100, ${flashAlpha * 0.5})`,
          glow: true,
          glowColor: COLORS.YELLOW,
          glowSize: 30 * flashAlpha,
        }
      );
    }
  }

  private renderDashEffect(effect: { position: { x: number; y: number }; direction: number; radius?: number }, alpha: number): void {
    const { position, direction, radius = 100 } = effect;
    
    // Trail effect
    const trailEnd = {
      x: position.x - Math.cos(direction) * radius,
      y: position.y - Math.sin(direction) * radius,
    };

    this.renderer.drawLine(
      trailEnd.x,
      trailEnd.y,
      position.x,
      position.y,
      {
        color: `rgba(255, 100, 100, ${alpha * 0.8})`,
        width: 8,
        glow: true,
        glowColor: COLORS.MAGENTA,
        glowSize: 15,
      }
    );
  }

  private renderVisualEffects(interpAlpha: number): void {
    const effects = this.world.getVisualEffects();
    const currentTime = this.world.getTime();

    // Debug: Check if there are multiple barrier effects
    const barrierEffects = effects.filter(e => e.type === VisualEffectType.BarrierWall);
    if (barrierEffects.length > 1) {
      console.warn(`[VFX] Multiple barrier effects: ${barrierEffects.length}`);
    }

    for (const effect of effects) {
      const elapsed = currentTime - effect.startTime;
      const progress = Math.max(0, Math.min(1, elapsed / effect.duration));
      const fadeAlpha = Math.max(0, Math.min(1, 1 - progress));

      // Get position and direction - follow player if followPlayerId is set
      let effectPosition = effect.position;
      let effectDirection = effect.direction || 0;
      
      if (effect.followPlayerId) {
        const player = this.world.getPlayer(effect.followPlayerId);
        if (player && player.alive) {
          // Use interpolated position for smooth movement (same as player rendering)
          effectPosition = {
            x: lerp(player.prevPosition.x, player.position.x, interpAlpha),
            y: lerp(player.prevPosition.y, player.position.y, interpAlpha)
          };
          effectDirection = player.rotation;
        }
      }

      switch (effect.type) {
        case VisualEffectType.Blink:
        case VisualEffectType.BlinkTrail:
          this.renderBlinkEffect(effectPosition, fadeAlpha);
          break;

        case VisualEffectType.Shockwave:
          this.renderNewShockwaveEffect(effectPosition, progress, effect.radius || 128);
          break;

        case VisualEffectType.Shield:
          this.renderNewShieldEffect(effectPosition, fadeAlpha, effect.color || COLORS.CYAN);
          break;

        case VisualEffectType.DomeShield:
          this.renderDomeShieldEffect(effectPosition, fadeAlpha, effect.radius || 160);
          break;

        case VisualEffectType.BarrierWall:
          this.renderBarrierWallEffect(effectPosition, effectDirection, fadeAlpha, effect.color || COLORS.CYAN);
          break;

        case VisualEffectType.HealBurst:
        case VisualEffectType.HealingField:
          this.renderHealingEffect(effectPosition, progress, effect.radius || 160);
          break;

        case VisualEffectType.PoisonCloud:
          this.renderPoisonCloudEffect(effect.position, fadeAlpha, effect.radius || 128);
          break;

        case VisualEffectType.Explosion:
          this.renderExplosionEffect(effect.position, progress, effect.radius || 80);
          break;

        case VisualEffectType.DashSlash:
          this.renderDashSlashEffect(effect.position, effect.direction || 0, fadeAlpha);
          break;

        case VisualEffectType.TurretSpawn:
          this.renderTurretSpawnEffect(effect.position, fadeAlpha);
          break;

        case VisualEffectType.Cloak:
          // Cloak is handled in player rendering
          break;

        case VisualEffectType.Reveal:
          this.renderRevealEffect(effect.position, progress);
          break;
      }
    }
  }

  private renderBlinkEffect(position: Vector2, alpha: number): void {
    // Teleport flash effect
    this.renderer.drawCircle(position.x, position.y, 30 * alpha, {
      fill: `rgba(100, 200, 255, ${alpha * 0.5})`,
      stroke: `rgba(150, 230, 255, ${alpha})`,
      strokeWidth: 3,
      glow: true,
      glowColor: COLORS.CYAN,
      glowSize: 20 * alpha,
    });
    
    // Particles
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const dist = 20 + (1 - alpha) * 40;
      const px = position.x + Math.cos(angle) * dist;
      const py = position.y + Math.sin(angle) * dist;
      this.renderer.drawCircle(px, py, 4 * alpha, {
        fill: COLORS.CYAN,
        glow: true,
        glowColor: COLORS.CYAN,
        glowSize: 5,
      });
    }
  }

  private renderNewShockwaveEffect(position: Vector2, progress: number, maxRadius: number): void {
    const radius = maxRadius * progress;
    const alpha = 1 - progress;
    
    // Expanding ring
    this.renderer.drawCircle(position.x, position.y, radius, {
      stroke: `rgba(255, 150, 50, ${alpha * 0.9})`,
      strokeWidth: 8 * (1 - progress),
      glow: true,
      glowColor: '#ff9933',
      glowSize: 20 * alpha,
    });
    
    // Secondary inner ring
    if (progress < 0.5) {
      const innerAlpha = 1 - (progress / 0.5);
      this.renderer.drawCircle(position.x, position.y, radius * 0.6, {
        stroke: `rgba(255, 200, 100, ${innerAlpha * 0.7})`,
        strokeWidth: 4,
      });
    }
  }

  private renderNewShieldEffect(position: Vector2, alpha: number, color: string): void {
    // Shield bubble
    this.renderer.drawCircle(position.x, position.y, 35, {
      fill: `rgba(100, 200, 255, ${alpha * 0.2})`,
      stroke: color,
      strokeWidth: 3 * alpha,
      glow: true,
      glowColor: color,
      glowSize: 15 * alpha,
    });
  }

  private renderDomeShieldEffect(position: Vector2, alpha: number, radius: number): void {
    // Large dome shield
    this.renderer.drawCircle(position.x, position.y, radius, {
      fill: `rgba(100, 200, 255, ${alpha * 0.15})`,
      stroke: `rgba(150, 230, 255, ${alpha * 0.8})`,
      strokeWidth: 4,
      glow: true,
      glowColor: COLORS.CYAN,
      glowSize: 25 * alpha,
    });
    
    // Hexagonal pattern overlay
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const px = position.x + Math.cos(angle) * radius * 0.7;
      const py = position.y + Math.sin(angle) * radius * 0.7;
      this.renderer.drawCircle(px, py, 8, {
        stroke: `rgba(200, 240, 255, ${alpha * 0.5})`,
        strokeWidth: 2,
      });
    }
  }

  private renderBarrierWallEffect(position: Vector2, direction: number, alpha: number, color: string): void {
    // Ensure alpha is clamped (defensive)
    const safeAlpha = Math.max(0, Math.min(1, alpha));
    
    // Front-facing arc shield (120 degree arc in front of player)
    const radius = 45;
    const arcAngle = Math.PI * (2/3); // 120 degrees
    const startAngle = direction - arcAngle / 2;
    const endAngle = direction + arcAngle / 2;
    
    const ctx = this.renderer.getContext();
    ctx.save();
    
    // Draw the arc shield
    ctx.beginPath();
    ctx.arc(position.x, position.y, radius, startAngle, endAngle);
    ctx.strokeStyle = color;
    ctx.lineWidth = 8 * safeAlpha;
    ctx.lineCap = 'round';
    
    // Glow effect
    ctx.shadowColor = color;
    ctx.shadowBlur = 20 * safeAlpha;
    ctx.stroke();
    
    // Inner lighter arc
    ctx.beginPath();
    ctx.arc(position.x, position.y, radius - 4, startAngle + 0.1, endAngle - 0.1);
    ctx.strokeStyle = `rgba(255, 255, 255, ${safeAlpha * 0.6})`;
    ctx.lineWidth = 3 * safeAlpha;
    ctx.stroke();
    
    // Add small energy nodes at ends of arc
    for (const angle of [startAngle, endAngle]) {
      const nx = position.x + Math.cos(angle) * radius;
      const ny = position.y + Math.sin(angle) * radius;
      ctx.beginPath();
      ctx.arc(nx, ny, 6 * safeAlpha, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    }
    
    // Add energy segments along the arc
    const segments = 5;
    for (let i = 1; i < segments; i++) {
      const segAngle = startAngle + (arcAngle * i / segments);
      const sx = position.x + Math.cos(segAngle) * radius;
      const sy = position.y + Math.sin(segAngle) * radius;
      ctx.beginPath();
      ctx.arc(sx, sy, 3 * safeAlpha, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${safeAlpha * 0.8})`;
      ctx.fill();
    }
    
    ctx.restore();
  }

  private renderHealingEffect(position: Vector2, progress: number, radius: number): void {
    const alpha = 1 - progress * 0.5; // Fade slower
    
    // Healing field circle
    this.renderer.drawCircle(position.x, position.y, radius, {
      fill: `rgba(100, 255, 150, ${alpha * 0.1})`,
      stroke: `rgba(100, 255, 150, ${alpha * 0.6})`,
      strokeWidth: 2,
    });
    
    // Rising heal particles
    const particleCount = 6;
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2 + progress * 2;
      const dist = radius * 0.5;
      const px = position.x + Math.cos(angle) * dist;
      const py = position.y + Math.sin(angle) * dist - progress * 30;
      this.renderer.drawText('+', px, py, {
        color: `rgba(100, 255, 150, ${alpha})`,
        size: 16,
        align: 'center',
        baseline: 'middle',
      });
    }
  }

  private renderPoisonCloudEffect(position: Vector2, alpha: number, radius: number): void {
    // Toxic cloud
    this.renderer.drawCircle(position.x, position.y, radius, {
      fill: `rgba(100, 200, 50, ${alpha * 0.3})`,
      stroke: `rgba(150, 220, 80, ${alpha * 0.6})`,
      strokeWidth: 3,
    });
    
    // Bubbling particles
    for (let i = 0; i < 5; i++) {
      const angle = (i / 5) * Math.PI * 2;
      const dist = radius * (0.3 + Math.random() * 0.5);
      const px = position.x + Math.cos(angle) * dist;
      const py = position.y + Math.sin(angle) * dist;
      this.renderer.drawCircle(px, py, 5 + Math.random() * 10, {
        fill: `rgba(120, 200, 60, ${alpha * 0.4})`,
      });
    }
  }

  private renderExplosionEffect(position: Vector2, progress: number, radius: number): void {
    const currentRadius = radius * progress;
    const alpha = 1 - progress;
    
    // Main explosion
    this.renderer.drawCircle(position.x, position.y, currentRadius, {
      fill: `rgba(255, 150, 50, ${alpha * 0.6})`,
      glow: true,
      glowColor: '#ff6600',
      glowSize: 30 * alpha,
    });
    
    // Core
    if (progress < 0.3) {
      const coreAlpha = 1 - (progress / 0.3);
      this.renderer.drawCircle(position.x, position.y, currentRadius * 0.5, {
        fill: `rgba(255, 255, 200, ${coreAlpha})`,
      });
    }
  }

  private renderDashSlashEffect(position: Vector2, direction: number, alpha: number): void {
    // Slash arc
    const arcStart = direction - Math.PI / 4;
    const arcEnd = direction + Math.PI / 4;
    
    this.renderer.drawArc(position.x, position.y, 40, arcStart, arcEnd, {
      stroke: `rgba(255, 100, 100, ${alpha})`,
      strokeWidth: 6 * alpha,
      glow: true,
      glowColor: COLORS.MAGENTA,
      glowSize: 15 * alpha,
    });
  }

  private renderTurretSpawnEffect(position: Vector2, alpha: number): void {
    // Spawn ring
    this.renderer.drawCircle(position.x, position.y, 25, {
      stroke: `rgba(255, 200, 100, ${alpha})`,
      strokeWidth: 3,
      glow: true,
      glowColor: COLORS.YELLOW,
      glowSize: 10 * alpha,
    });
    
    // Holographic grid
    for (let i = -1; i <= 1; i++) {
      this.renderer.drawLine(
        position.x - 20, position.y + i * 10,
        position.x + 20, position.y + i * 10,
        { color: `rgba(255, 200, 100, ${alpha * 0.5})`, width: 1 }
      );
      this.renderer.drawLine(
        position.x + i * 10, position.y - 20,
        position.x + i * 10, position.y + 20,
        { color: `rgba(255, 200, 100, ${alpha * 0.5})`, width: 1 }
      );
    }
  }

  private renderRevealEffect(position: Vector2, progress: number): void {
    const radius = 50 + progress * 100;
    const alpha = 1 - progress;
    
    this.renderer.drawCircle(position.x, position.y, radius, {
      stroke: `rgba(255, 50, 50, ${alpha * 0.8})`,
      strokeWidth: 2,
    });
  }

  private renderBulletTracers(): void {
    const now = performance.now();
    
    // Filter out old tracers (older than 200ms)
    this.bulletTracers = this.bulletTracers.filter(tracer => now - tracer.time < 200);

    for (const tracer of this.bulletTracers) {
      const age = now - tracer.time;
      const alpha = 1 - (age / 200);
      
      // Different colors for player vs enemy
      const tracerColor = tracer.isEnemy 
        ? `rgba(255, 80, 80, ${alpha * 0.9})`   // Red for enemies
        : `rgba(100, 255, 255, ${alpha * 0.9})`; // Cyan for player
      const impactColor = tracer.isEnemy
        ? `rgba(255, 100, 100, ${alpha * 0.8})`
        : `rgba(100, 255, 200, ${alpha * 0.8})`;
      const glowColor = tracer.isEnemy ? COLORS.ATTACKER : COLORS.CYAN;
      
      // Draw tracer line
      this.renderer.drawLine(
        tracer.start.x, tracer.start.y,
        tracer.end.x, tracer.end.y,
        {
          color: tracerColor,
          width: 2,
        }
      );
      
      // Draw impact point
      this.renderer.drawCircle(tracer.end.x, tracer.end.y, 5 * alpha, {
        fill: impactColor,
        glow: true,
        glowColor: glowColor,
        glowSize: 10 * alpha,
      });
    }
  }

  addMuzzleFlash(position: Vector2, direction: number): void {
    this.muzzleFlashes.push({
      position: { ...position },
      direction,
      time: performance.now(),
    });
    // Add muzzle flash particles
    this.particleSystem.muzzleFlashParticles(position, direction);
  }

  addBulletTracer(start: Vector2, end: Vector2, isEnemy: boolean = false): void {
    this.bulletTracers.push({
      start: { ...start },
      end: { ...end },
      time: performance.now(),
      isEnemy,
    });
    // Add bullet impact particles at end point
    const direction = { x: end.x - start.x, y: end.y - start.y };
    const len = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    if (len > 0) {
      const normal = { x: -direction.x / len, y: -direction.y / len };
      this.particleSystem.bulletImpact(end, normal);
    }
  }

  // Particle effect methods
  addPlayerDeath(position: Vector2): void {
    this.particleSystem.playerDeath(position);
  }

  addPlayerHit(position: Vector2, direction: Vector2): void {
    this.particleSystem.bulletHitPlayer(position, direction);
  }

  addWeaponSwitch(position: Vector2): void {
    this.particleSystem.weaponSwitch(position);
  }

  addPickupNotification(message: string, color: string = '#00FF88'): void {
    this.pickupNotifications.push({
      message,
      time: performance.now(),
      duration: 3000, // 3 seconds
      color,
    });
  }

  updateParticles(dt: number): void {
    this.particleSystem.update(dt);
  }

  private renderUI(): void {
    const width = this.renderer.getWidth();
    const height = this.renderer.getHeight();
    const localPlayer = this.world.getLocalPlayer();

    // Game pause overlay (tab focus sync)
    if (this.world.isGamePaused()) {
      this.renderGamePauseOverlay(width, height);
    }

    // Top bar - Score and time
    this.renderTopBar(width);

    // Pickup notifications (top-right, below score)
    this.renderPickupNotifications(width);

    // Bottom bar - Health, ammo, abilities
    if (localPlayer && localPlayer.alive) {
      this.renderBottomBar(localPlayer, width, height);
    }

    // Crosshair
    this.renderCrosshair(width, height);

    // Round state overlay
    if (this.world.getPhase() !== GamePhase.RoundActive) {
      this.renderPhaseOverlay(width, height);
    }
  }

  private renderGamePauseOverlay(width: number, height: number): void {
    const pausedBy = this.world.getGamePausedBy();
    
    // Semi-transparent dark overlay
    this.renderer.drawRect(0, 0, width, height, { fill: 'rgba(0, 0, 0, 0.7)' });
    
    // Pause icon (two vertical bars)
    const iconX = width / 2;
    const iconY = height / 2 - 60;
    const barWidth = 12;
    const barHeight = 40;
    const gap = 10;
    
    this.renderer.drawRect(iconX - gap - barWidth, iconY - barHeight / 2, barWidth, barHeight, {
      fill: COLORS.YELLOW,
      glow: true,
      glowColor: COLORS.YELLOW,
      glowSize: 10,
    });
    this.renderer.drawRect(iconX + gap, iconY - barHeight / 2, barWidth, barHeight, {
      fill: COLORS.YELLOW,
      glow: true,
      glowColor: COLORS.YELLOW,
      glowSize: 10,
    });
    
    // Paused text
    this.renderer.drawText(
      'GAME PAUSED',
      width / 2, height / 2,
      {
        color: COLORS.YELLOW,
        align: 'center',
        baseline: 'middle',
        size: 32,
        glow: true,
        glowColor: COLORS.YELLOW,
        glowSize: 15,
      }
    );
    
    // Who paused
    this.renderer.drawText(
      `${pausedBy} is away`,
      width / 2, height / 2 + 40,
      { color: '#aaa', align: 'center', baseline: 'middle', size: 18 }
    );
    
    // Instructions
    this.renderer.drawText(
      'Waiting for all players to return...',
      width / 2, height / 2 + 70,
      { color: '#666', align: 'center', baseline: 'middle', size: 14 }
    );
  }

  private renderPickupNotifications(width: number): void {
    const now = performance.now();
    const startY = 60; // Below top bar
    const lineHeight = 35;
    
    // Remove expired notifications
    this.pickupNotifications = this.pickupNotifications.filter(n => now - n.time < n.duration);
    
    // Render active notifications
    this.pickupNotifications.forEach((notification, index) => {
      const elapsed = now - notification.time;
      const alpha = elapsed < 500 ? elapsed / 500 : (notification.duration - elapsed) / 500;
      const y = startY + index * lineHeight;
      
      // Background bubble
      const textWidth = notification.message.length * 10;
      const padding = 15;
      const bgWidth = textWidth + padding * 2;
      const bgX = width - bgWidth - 20;
      
      this.renderer.drawRect(
        bgX, y - 12,
        bgWidth, 24,
        {
          fill: `rgba(0, 0, 0, ${Math.min(0.8, alpha)})`,
          stroke: notification.color,
          strokeWidth: 2,
        }
      );
      
      // Text
      this.renderer.drawText(
        notification.message,
        width - 20 - padding, y,
        {
          color: notification.color,
          align: 'right',
          baseline: 'middle',
          size: 14,
        }
      );
    });
  }

  private renderTopBar(width: number): void {
    const score = this.world.getScore();
    const roundTime = this.world.getTime();
    const roundTimeLimit = 180000; // 3 minutes
    const remaining = Math.max(0, roundTimeLimit - roundTime);
    const objectives = this.world.getObjectives();
    const localPlayer = this.world.getLocalPlayer();
    const map = this.world.getMap();

    // Background
    this.renderer.drawRect(0, 0, width, 40, { fill: 'rgba(0,0,0,0.7)' });

    // Map name and Hero name on the left side
    this.renderer.drawText(
      `Map: ${map.data.name}`,
      15, 14,
      { color: '#888', align: 'left', baseline: 'middle', size: 11 }
    );
    
    if (localPlayer) {
      const heroDef = HEROES[localPlayer.heroId];
      this.renderer.drawText(
        heroDef.name,
        15, 28,
        { color: COLORS.CYAN, align: 'left', baseline: 'middle', size: 12 }
      );
    }

    // Attackers score
    this.renderer.drawText(
      `${score.attackers}`,
      width / 2 - 60, 20,
      { color: COLORS.ATTACKER, align: 'center', baseline: 'middle', size: 24 }
    );

    // Timer
    this.renderer.drawText(
      formatTime(remaining),
      width / 2, 20,
      { color: COLORS.CYAN, align: 'center', baseline: 'middle', size: 20 }
    );

    // Defenders score
    this.renderer.drawText(
      `${score.defenders}`,
      width / 2 + 60, 20,
      { color: COLORS.DEFENDER, align: 'center', baseline: 'middle', size: 24 }
    );

    // Version info (below top bar, right side - avoid spike status box)
    this.renderer.drawText(
      `v${this.appVersion}`,
      width - 10, 48,
      { color: 'rgba(255,255,255,0.4)', align: 'right', baseline: 'middle', size: 9 }
    );

    // Round number
    this.renderer.drawText(
      `Round ${this.world.getRoundNumber()}`,
      width / 2, 35,
      { color: '#888', align: 'center', baseline: 'middle', size: 10 }
    );

    // Spike status (Data Spike mode)
    if (objectives.spikeCarrierId || objectives.spikePlanted) {
      const spikeX = width - 150;
      
      if (objectives.spikePlanted) {
        // Spike is planted - show upload countdown
        const uploadRemaining = Math.max(0, 45000 - (this.world.getAbsoluteTime() - objectives.spikePlantTime));
        const pulse = Math.sin(this.world.getAbsoluteTime() * 0.015) * 0.3 + 0.7;
        
        // Flashing background for urgency
        this.renderer.drawRect(spikeX - 10, 5, 160, 30, {
          fill: `rgba(255, 0, 0, ${pulse * 0.3})`,
          stroke: '#ff4444',
          strokeWidth: 2,
        });
        
        this.renderer.drawText(
          `💣 SPIKE PLANTED`,
          spikeX + 70, 13,
          { color: '#ff4444', align: 'center', size: 12 }
        );
        
        this.renderer.drawText(
          `${(uploadRemaining / 1000).toFixed(1)}s`,
          spikeX + 70, 27,
          { color: '#ffffff', align: 'center', size: 14 }
        );
      } else if (objectives.spikeCarrierId) {
        // Spike not planted - show carrier status
        const carrier = this.world.getPlayers().find(p => p.id === objectives.spikeCarrierId);
        const isLocalCarrier = localPlayer && objectives.spikeCarrierId === localPlayer.id;
        
        this.renderer.drawRect(spikeX - 10, 5, 160, 30, {
          fill: 'rgba(0, 0, 0, 0.5)',
          stroke: isLocalCarrier ? '#ff8800' : '#666',
          strokeWidth: 1,
        });
        
        if (isLocalCarrier) {
          this.renderer.drawText(
            `💣 YOU HAVE SPIKE`,
            spikeX + 70, 13,
            { color: '#ff8800', align: 'center', size: 11 }
          );
          this.renderer.drawText(
            `Hold E in site to plant`,
            spikeX + 70, 27,
            { color: '#aaa', align: 'center', size: 9 }
          );
        } else if (carrier) {
          this.renderer.drawText(
            `💣 ${carrier.name}`,
            spikeX + 70, 20,
            { color: '#888', align: 'center', size: 11 }
          );
        }
      }
    }
  }

  private renderBottomBar(player: Player, width: number, height: number): void {
    const barHeight = 80;
    const y = height - barHeight;

    // Background
    this.renderer.drawRect(0, y, width, barHeight, { fill: 'rgba(0,0,0,0.7)' });

    // Health bar (left side)
    this.renderer.drawHealthBar(
      20, y + 20,
      200, 20,
      player.health, player.maxHealth,
      player.shield, player.maxShield
    );

    // Health number with dark color for visibility over shield
    this.renderer.drawText(
      `${Math.ceil(player.health)}`,
      120, y + 30,
      { color: '#222', align: 'center', baseline: 'middle', size: 14 }
    );

    // Weapon inventory slots (1-10) - positioned next to health bar
    const slotSize = 28;
    const slotGap = 4;
    const slotsStartX = 240; // Right after health bar (20 + 200 + 20 margin)
    
    for (let i = 0; i < 10; i++) {
      const slotX = slotsStartX + i * (slotSize + slotGap);
      const slotY = y + 15;
      const hasWeapon = player.weaponInventory[i] !== null;
      const isActive = player.currentWeaponSlot === i;
      
      // Slot background
      this.renderer.drawRect(slotX, slotY, slotSize, slotSize, {
        fill: isActive ? 'rgba(12, 229, 240, 0.3)' : 'rgba(30, 30, 40, 0.8)',
        stroke: isActive ? COLORS.CYAN : (hasWeapon ? '#555' : '#333'),
        strokeWidth: isActive ? 2 : 1,
      });
      
      // Slot number (1-9, then 0 for slot 10)
      const displayNumber = i === 9 ? 0 : i + 1;
      this.renderer.drawText(
        `${displayNumber}`,
        slotX + slotSize / 2,
        slotY + slotSize / 2,
        { 
          color: hasWeapon ? (isActive ? COLORS.CYAN : '#aaa') : '#444', 
          align: 'center', 
          baseline: 'middle', 
          size: 11 
        }
      );
      
      // Weapon icon (first letter of weapon name)
      if (hasWeapon) {
        const weaponState = player.weaponInventory[i]!;
        const initial = weaponState.weaponId.charAt(0).toUpperCase();
        this.renderer.drawText(
          initial,
          slotX + slotSize / 2,
          slotY + slotSize + 8,
          { color: '#666', align: 'center', size: 8 }
        );
      }
    }

    // Current weapon info (positioned between weapon slots and credits)
    const weaponInfoX = 600;
    this.renderer.drawText(
      player.weapon.weaponId.toUpperCase().replace('_', ' '),
      weaponInfoX, y + 20,
      { color: '#fff', align: 'left', size: 14 }
    );

    const ammoText = player.weapon.reloading 
      ? 'RELOADING...'
      : `${player.weapon.ammo} / ${player.weapon.maxAmmo}`;
    
    this.renderer.drawText(
      ammoText,
      weaponInfoX, y + 45,
      { color: player.weapon.reloading ? COLORS.YELLOW : COLORS.CYAN, align: 'left', size: 18 }
    );

    // Grenade count (positioned next to ammo)
    const grenadeX = weaponInfoX + 150;
    this.renderer.drawText(
      'GRENADES',
      grenadeX, y + 20,
      { color: '#888', align: 'left', size: 10 }
    );
    
    // Draw grenade icon and count
    const grenadeIconX = grenadeX + 10;
    const grenadeIconY = y + 40;
    this.renderer.drawCircle(grenadeIconX, grenadeIconY, 8, {
      fill: player.grenadeCount > 0 ? '#2a2a2a' : '#1a1a1a',
      stroke: player.grenadeCount > 0 ? '#ff4444' : '#333',
      strokeWidth: 2,
    });
    
    this.renderer.drawText(
      `${player.grenadeCount}`,
      grenadeIconX + 20, grenadeIconY,
      { color: player.grenadeCount > 0 ? COLORS.YELLOW : '#555', align: 'left', baseline: 'middle', size: 16 }
    );
    
    this.renderer.drawText(
      'G',
      grenadeIconX + 40, grenadeIconY,
      { color: '#666', align: 'left', baseline: 'middle', size: 10 }
    );

    // Radio Calls - Bomb Call and Missile Strike
    const radioX = grenadeX + 120;
    
    // Bomb Call Radio (B-52 Bomber)
    const bombIconX = radioX;
    const bombIconY = y + 40;
    this.renderer.drawRect(
      bombIconX - 10, bombIconY - 8,
      20, 16,
      { 
        fill: player.bombCallCount > 0 ? '#1a1a2a' : '#0a0a12',
        stroke: player.bombCallCount > 0 ? COLORS.MAGENTA : '#333',
        strokeWidth: 2 
      }
    );
    
    // Radio antenna
    this.renderer.drawLine(
      bombIconX, bombIconY - 8,
      bombIconX, bombIconY - 14,
      { color: player.bombCallCount > 0 ? COLORS.MAGENTA : '#333', width: 2 }
    );
    
    this.renderer.drawText(
      player.bombCallCount > 0 ? '✈' : '✗',
      bombIconX, bombIconY,
      { color: player.bombCallCount > 0 ? COLORS.MAGENTA : '#555', align: 'center', baseline: 'middle', size: 12 }
    );
    
    this.renderer.drawText(
      'V',
      bombIconX, bombIconY + 18,
      { color: '#666', align: 'center', size: 10 }
    );
    
    // Missile Strike Radio
    const missileIconX = radioX + 40;
    const missileIconY = y + 40;
    this.renderer.drawRect(
      missileIconX - 10, missileIconY - 8,
      20, 16,
      { 
        fill: player.missileStrikeCount > 0 ? '#1a2a1a' : '#0a0a12',
        stroke: player.missileStrikeCount > 0 ? '#ff6600' : '#333',
        strokeWidth: 2 
      }
    );
    
    // Radio antenna
    this.renderer.drawLine(
      missileIconX, missileIconY - 8,
      missileIconX, missileIconY - 14,
      { color: player.missileStrikeCount > 0 ? '#ff6600' : '#333', width: 2 }
    );
    
    this.renderer.drawText(
      player.missileStrikeCount > 0 ? '🚀' : '✗',
      missileIconX, missileIconY,
      { color: player.missileStrikeCount > 0 ? '#ff6600' : '#555', align: 'center', baseline: 'middle', size: 12 }
    );
    
    this.renderer.drawText(
      'J',
      missileIconX, missileIconY + 18,
      { color: '#666', align: 'center', size: 10 }
    );

    // Abilities UI - Enhanced with icons and better visuals
    this.renderAbilities(player, width, y);
  }

  private renderCrosshair(width: number, height: number): void {
    const cx = width / 2;
    const cy = height / 2;
    const size = 10;
    const gap = 4;

    // Crosshair lines
    this.renderer.drawLine(cx - size, cy, cx - gap, cy, { color: COLORS.CYAN, width: 2 });
    this.renderer.drawLine(cx + gap, cy, cx + size, cy, { color: COLORS.CYAN, width: 2 });
    this.renderer.drawLine(cx, cy - size, cx, cy - gap, { color: COLORS.CYAN, width: 2 });
    this.renderer.drawLine(cx, cy + gap, cx, cy + size, { color: COLORS.CYAN, width: 2 });

    // Center dot
    this.renderer.drawCircle(cx, cy, 2, { fill: COLORS.CYAN });
  }

  private renderAbilities(player: Player, width: number, barY: number): void {
    const heroDef = HEROES[player.heroId];
    const now = this.world.getTime();
    
    // Ability icons/symbols for each hero
    // Order: [Passive, Q ability (index 1), F ability (index 2), Ultimate (index 3)]
    const abilityIcons: Record<string, string[]> = {
      'Spectre': ['👻', '🌑', '📡', '🌀'],   // Ghost Steps, Cloak, Sensor Jammer, Shadow Dive
      'Byte': ['⚡', '💥', '🕸️', '🎮'],       // QuickJack, EMP Pulse, Hack Trap, Override
      'Vanta': ['🛡️', '🔰', '💢', '⚡'],      // Titan Armor, Fortify(Q), Shockwave(F), Overclock
      'Katana': ['💨', '⚔️', '🌟', '🌪️'],    // Sprint Boost, Dash Slash, Shuriken Burst, Blade Storm
      'Glyph': ['🔍', '⚡', '📍', '🐝'],      // Recon Drone, Shock Drone, Ping Pulse, Nano Swarm
      'Spark': ['🔥', '🔥', '💨', '🌋'],      // Fireproof, Plasma Fire, Thermal Burst, Thermal Overdrive
      'Zero': ['🎯', '👁️', '↩️', '⚡'],      // Scoped Precision, Mark Target, Dash Back, Rail Beam
      'Mira': ['💚', '💉', '🛡️', '✨'],      // Auto-Heal, Nano-Heal, Shield, Revive
    };
    
    const icons = abilityIcons[heroDef.name] || ['⭐', '⭐', '⭐', '⭐'];
    const keys = ['', 'Q', 'F', 'X'];  // Index 0 = Passive (no key), 1 = Q, 2 = F, 3 = X (Ultimate)
    
    // Layout constants
    const abilitySize = 48;
    const abilityGap = 8;
    const totalWidth = 4 * abilitySize + 3 * abilityGap;
    const startX = width - totalWidth - 20;
    const ay = barY + 16;
    
    // Credits display - left of abilities
    const creditsX = startX - 70;
    this.renderer.drawRect(creditsX - 30, ay + 8, 60, 32, {
      fill: 'rgba(30, 30, 40, 0.8)',
      stroke: COLORS.YELLOW,
      strokeWidth: 1,
    });
    this.renderer.drawText(
      `$${player.credits}`,
      creditsX, ay + 24,
      { color: COLORS.YELLOW, align: 'center', baseline: 'middle', size: 16 }
    );
    
    for (let i = 0; i < 4; i++) {
      const ability = player.abilities[i];
      const abilityDef = heroDef.abilities[i];
      if (!ability || !abilityDef) continue;
      
      const ax = startX + i * (abilitySize + abilityGap);
      const isPassive = i === 0;
      const isUltimate = i === 3;
      
      // Calculate cooldown
      const cooldownRemaining = Math.max(0, ability.cooldownEndTime - now);
      const cooldownPercent = abilityDef.cooldown > 0 ? cooldownRemaining / abilityDef.cooldown : 0;
      const isReady = cooldownRemaining === 0;
      const hasCharges = ability.charges > 0;
      
      // Background shape - hexagon for ultimate, rounded rect for others
      if (isUltimate) {
        this.drawHexagon(ax + abilitySize / 2, ay + abilitySize / 2, abilitySize / 2, {
          fill: isReady ? 'rgba(255, 200, 0, 0.3)' : 'rgba(30, 30, 40, 0.9)',
          stroke: isReady ? COLORS.YELLOW : '#444',
          strokeWidth: 2,
        });
      } else if (isPassive) {
        // Passive - always active, dimmer style
        this.renderer.drawRect(ax, ay, abilitySize, abilitySize, {
          fill: 'rgba(50, 80, 50, 0.6)',
          stroke: '#4a4',
          strokeWidth: 1,
        });
      } else {
        // Regular ability
        this.renderer.drawRect(ax, ay, abilitySize, abilitySize, {
          fill: isReady && hasCharges ? 'rgba(12, 229, 240, 0.2)' : 'rgba(30, 30, 40, 0.9)',
          stroke: isReady && hasCharges ? COLORS.CYAN : '#444',
          strokeWidth: isReady && hasCharges ? 2 : 1,
        });
      }
      
      // Cooldown overlay (pie chart style)
      if (cooldownPercent > 0 && !isPassive) {
        const cx = ax + abilitySize / 2;
        const cy = ay + abilitySize / 2;
        const radius = abilitySize / 2 - 2;
        
        this.renderer.getContext().save();
        this.renderer.getContext().globalAlpha = 0.7;
        this.renderer.getContext().fillStyle = '#000';
        this.renderer.getContext().beginPath();
        this.renderer.getContext().moveTo(cx, cy);
        this.renderer.getContext().arc(
          cx, cy, radius,
          -Math.PI / 2,
          -Math.PI / 2 + Math.PI * 2 * cooldownPercent,
          false
        );
        this.renderer.getContext().closePath();
        this.renderer.getContext().fill();
        this.renderer.getContext().restore();
        
        // Cooldown time remaining text
        const secondsLeft = Math.ceil(cooldownRemaining / 1000);
        this.renderer.drawText(
          `${secondsLeft}`,
          ax + abilitySize / 2,
          ay + abilitySize / 2,
          { color: '#fff', align: 'center', baseline: 'middle', size: 16 }
        );
      } else {
        // Ability icon
        this.renderer.drawText(
          icons[i] ?? '⭐',
          ax + abilitySize / 2,
          ay + abilitySize / 2,
          { color: '#fff', align: 'center', baseline: 'middle', size: 20 }
        );
      }
      
      // Key binding (top-left corner)
      const keyColor = isPassive ? '#4a4' : (isReady && hasCharges ? COLORS.CYAN : '#666');
      this.renderer.drawText(
        keys[i] ?? '',
        ax + 8,
        ay + 10,
        { color: keyColor, align: 'center', baseline: 'middle', size: 10 }
      );
      
      // Charges indicator (if more than 1 max charge)
      if (abilityDef.charges > 1) {
        const chargeText = `${ability.charges}/${abilityDef.charges}`;
        this.renderer.drawText(
          chargeText,
          ax + abilitySize - 8,
          ay + abilitySize - 6,
          { color: ability.charges > 0 ? COLORS.CYAN : '#666', align: 'center', baseline: 'middle', size: 9 }
        );
      }
      
      // Ultimate charge indicator
      if (isUltimate) {
        const ultCharge = ability.ultimateCharge || 0;
        const ultCost = abilityDef.ultimateCost || 6;
        const ultPercent = Math.min(1, ultCharge / ultCost);
        
        // Draw charge bar under ultimate
        const barWidth = abilitySize - 4;
        const barHeight = 4;
        const barX = ax + 2;
        const barYPos = ay + abilitySize + 2;
        
        // Background
        this.renderer.drawRect(barX, barYPos, barWidth, barHeight, { fill: '#222' });
        
        // Fill
        const ultReady = ultCharge >= ultCost;
        this.renderer.drawRect(barX, barYPos, barWidth * ultPercent, barHeight, { 
          fill: ultReady ? COLORS.YELLOW : COLORS.CYAN 
        });
        
        // Charge text
        if (!ultReady) {
          this.renderer.drawText(
            `${Math.floor(ultCharge)}/${ultCost}`,
            ax + abilitySize / 2,
            barYPos + barHeight + 8,
            { color: '#888', align: 'center', baseline: 'middle', size: 8 }
          );
        } else {
          this.renderer.drawText(
            'READY',
            ax + abilitySize / 2,
            barYPos + barHeight + 8,
            { color: COLORS.YELLOW, align: 'center', baseline: 'middle', size: 8 }
          );
        }
      }
      
      // Ability name - always show under each ability (except ultimate which has charge bar)
      if (!isUltimate) {
        // Truncate long names
        const displayName = abilityDef.name.length > 10 
          ? abilityDef.name.substring(0, 9) + '…' 
          : abilityDef.name;
        this.renderer.drawText(
          displayName,
          ax + abilitySize / 2,
          ay + abilitySize + 8,
          { color: isPassive ? '#4a4' : '#888', align: 'center', baseline: 'middle', size: 7 }
        );
      }
    }
    
    // Hero name display
    this.renderer.drawText(
      heroDef.name.toUpperCase(),
      startX + totalWidth / 2,
      ay - 8,
      { color: COLORS.CYAN, align: 'center', baseline: 'middle', size: 12 }
    );
  }
  
  private drawHexagon(cx: number, cy: number, radius: number, options: { fill?: string; stroke?: string; strokeWidth?: number }): void {
    const ctx = this.renderer.getContext();
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const x = cx + radius * Math.cos(angle);
      const y = cy + radius * Math.sin(angle);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.closePath();
    
    if (options.fill) {
      ctx.fillStyle = options.fill;
      ctx.fill();
    }
    if (options.stroke) {
      ctx.strokeStyle = options.stroke;
      ctx.lineWidth = options.strokeWidth || 1;
      ctx.stroke();
    }
    ctx.restore();
  }

  private renderPhaseOverlay(width: number, height: number): void {
    const phase = this.world.getPhase();
    const isMatchEnd = phase === GamePhase.MatchEnd;
    let text = '';
    let subtext = '';
    let isVictory = false;
    let isDefeat = false;

    // Check if local player won or lost
    const localPlayer = this.world.getLocalPlayer();
    if (isMatchEnd && localPlayer) {
      const score = this.world.getScore();
      const attackersWon = score.attackers > score.defenders;
      isVictory = (localPlayer.team === Team.Attackers && attackersWon) || 
                  (localPlayer.team === Team.Defenders && !attackersWon);
      isDefeat = !isVictory;
    }

    // Trigger confetti on match end transition
    if (isMatchEnd && !this.lastMatchEndPhase) {
      this.matchEndStartTime = Date.now();
      if (isVictory) {
        this.spawnVictoryConfetti(width, height);
      }
    }
    this.lastMatchEndPhase = isMatchEnd;

    switch (phase) {
      case GamePhase.Lobby:
        text = 'WAITING FOR PLAYERS';
        subtext = 'Press SPACE to start when ready';
        break;
      case GamePhase.BuyPhase:
        text = 'BUY PHASE';
        subtext = 'Purchase weapons and abilities';
        break;
      case GamePhase.RoundEnd:
        const score = this.world.getScore();
        text = 'ROUND OVER';
        subtext = `Attackers: ${score.attackers} - Defenders: ${score.defenders}`;
        break;
      case GamePhase.MatchEnd:
        const finalScore = this.world.getScore();
        if (isVictory) {
          text = '🏆 VICTORY! 🏆';
        } else if (isDefeat) {
          text = '💀 DEFEAT 💀';
        } else {
          text = finalScore.attackers > finalScore.defenders ? 'ATTACKERS WIN' : 'DEFENDERS WIN';
        }
        subtext = `Final Score: ${finalScore.attackers} - ${finalScore.defenders}`;
        break;
    }

    // Darken background - different colors for victory/defeat
    if (isMatchEnd && isVictory) {
      this.renderer.drawRect(0, 0, width, height, { fill: 'rgba(0,40,0,0.6)' });
    } else if (isMatchEnd && isDefeat) {
      this.renderer.drawRect(0, 0, width, height, { fill: 'rgba(40,0,0,0.6)' });
    } else {
      this.renderer.drawRect(0, 0, width, height, { fill: 'rgba(0,0,0,0.5)' });
    }

    // Render confetti for victory
    if (isMatchEnd && isVictory) {
      this.updateAndRenderConfetti(width, height);
    }

    // Render defeat particles
    if (isMatchEnd && isDefeat) {
      this.renderDefeatEffect(width, height);
    }

    // Choose colors based on victory/defeat
    let mainColor: string = COLORS.CYAN;
    let glowSize = 20;
    if (isMatchEnd) {
      if (isVictory) {
        mainColor = '#ffd700'; // Gold
        glowSize = 30;
      } else if (isDefeat) {
        mainColor = '#ff4444'; // Red
        glowSize = 15;
      }
    }

    // Main text
    this.renderer.drawText(
      text,
      width / 2, height / 2 - 40,
      { 
        color: mainColor, 
        align: 'center', 
        baseline: 'middle', 
        size: isMatchEnd ? 48 : 36,
        glow: true,
        glowColor: mainColor,
        glowSize: glowSize,
      }
    );

    // Subtext
    this.renderer.drawText(
      subtext,
      width / 2, height / 2,
      { color: '#888', align: 'center', baseline: 'middle', size: 16 }
    );

    // Show countdown during RoundEnd phase
    if (phase === GamePhase.RoundEnd) {
      const countdownTime = this.world.getCountdownTime();
      const isPaused = this.world.isCountdownPaused();
      const pausedBy = this.world.getCountdownPausedBy();
      const seconds = Math.ceil(countdownTime / 1000);
      
      const countdownText = isPaused 
        ? `PAUSED by ${pausedBy}` 
        : `Next round in ${seconds}s`;
      
      const countdownColor = isPaused ? COLORS.YELLOW : COLORS.CYAN;
      
      this.renderer.drawText(
        countdownText,
        width / 2, height / 2 + 40,
        { 
          color: countdownColor, 
          align: 'center', 
          baseline: 'middle', 
          size: 24,
          glow: true,
          glowColor: countdownColor,
          glowSize: 10,
        }
      );

      // Instructions
      const instructionText = isPaused 
        ? 'Press P to resume' 
        : 'Press P to pause countdown';
      
      this.renderer.drawText(
        instructionText,
        width / 2, height / 2 + 70,
        { color: '#666', align: 'center', baseline: 'middle', size: 12 }
      );
    }
  }

  // Spawn confetti particles for victory celebration
  private spawnVictoryConfetti(width: number, _height: number): void {
    const confettiColors = [
      '#ffd700', // Gold
      '#ff6b6b', // Red
      '#4ecdc4', // Teal
      '#45b7d1', // Blue
      '#96ceb4', // Green
      '#ffeaa7', // Yellow
      '#dfe6e9', // White
      '#a29bfe', // Purple
      '#fd79a8', // Pink
      '#00b894', // Emerald
    ];

    // Spawn confetti from top
    for (let i = 0; i < 150; i++) {
      this.matchEndConfetti.push({
        x: Math.random() * width,
        y: -20 - Math.random() * 200,
        vx: (Math.random() - 0.5) * 200,
        vy: Math.random() * 300 + 100,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 10,
        width: Math.random() * 8 + 4,
        height: Math.random() * 12 + 6,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)]!,
        life: 5 + Math.random() * 3,
        maxLife: 5 + Math.random() * 3,
      });
    }
  }

  // Update and render confetti particles
  private updateAndRenderConfetti(width: number, _height: number): void {
    const ctx = this.renderer.getContext();
    const dt = 1 / 60; // Approximate frame time

    for (let i = this.matchEndConfetti.length - 1; i >= 0; i--) {
      const p = this.matchEndConfetti[i]!;
      
      // Update life
      p.life -= dt;
      if (p.life <= 0) {
        this.matchEndConfetti.splice(i, 1);
        continue;
      }

      // Apply gravity and wind
      p.vy += 400 * dt; // Gravity
      p.vx += Math.sin(Date.now() / 500 + p.x / 100) * 50 * dt; // Gentle wind sway

      // Update position
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.rotationSpeed * dt;

      // Air resistance
      p.vx *= 0.99;
      p.vy *= 0.995;

      // Wrap around horizontally
      if (p.x < -20) p.x = width + 20;
      if (p.x > width + 20) p.x = -20;

      // Render confetti
      const alpha = Math.min(1, p.life / (p.maxLife * 0.3));
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = alpha;
      ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Continuously spawn more confetti
    const timeSinceStart = Date.now() - this.matchEndStartTime;
    if (timeSinceStart < 5000 && Math.random() < 0.3) {
      const confettiColors = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#ffeaa7'];
      this.matchEndConfetti.push({
        x: Math.random() * width,
        y: -10,
        vx: (Math.random() - 0.5) * 150,
        vy: Math.random() * 200 + 100,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 8,
        width: Math.random() * 8 + 4,
        height: Math.random() * 12 + 6,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)]!,
        life: 4 + Math.random() * 2,
        maxLife: 4 + Math.random() * 2,
      });
    }
  }

  // Render defeat effect (falling/fading particles)
  private renderDefeatEffect(width: number, height: number): void {
    const ctx = this.renderer.getContext();
    const time = Date.now() - this.matchEndStartTime;
    
    // Pulsing red vignette
    const pulseIntensity = 0.3 + Math.sin(time / 500) * 0.1;
    const gradient = ctx.createRadialGradient(
      width / 2, height / 2, 0,
      width / 2, height / 2, Math.max(width, height) * 0.7
    );
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(0.5, 'rgba(80, 0, 0, 0.2)');
    gradient.addColorStop(1, `rgba(100, 0, 0, ${pulseIntensity})`);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Falling skull particles (simplified)
    const numSkulls = 8;
    for (let i = 0; i < numSkulls; i++) {
      const baseX = (width / numSkulls) * i + width / numSkulls / 2;
      const y = ((time / 20 + i * 100) % (height + 100)) - 50;
      const alpha = 0.3 + Math.sin(time / 300 + i) * 0.1;
      const size = 20 + Math.sin(i * 1.5) * 5;
      
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.font = `${size}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#444';
      ctx.fillText('💀', baseX + Math.sin(time / 400 + i * 2) * 20, y);
      ctx.restore();
    }
    ctx.globalAlpha = 1;

    // Cracked screen effect (static lines)
    ctx.strokeStyle = 'rgba(60, 0, 0, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(width * 0.3, 0);
    ctx.lineTo(width * 0.4, height * 0.3);
    ctx.lineTo(width * 0.35, height * 0.5);
    ctx.lineTo(width * 0.45, height);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(width * 0.7, 0);
    ctx.lineTo(width * 0.6, height * 0.4);
    ctx.lineTo(width * 0.65, height * 0.7);
    ctx.lineTo(width * 0.55, height);
    ctx.stroke();
  }

  updateWorld(world: GameWorld): void {
    this.world = world;
  }
}
